if (!customElements.get('agency-contact-drawer')) {
  // First touch belongs to this tab's current visit, not the browser's first-ever visit.
  // Keep campaign and landing together; a new campaign or 30 idle minutes starts a new visit.
  const VISIT_ATTRIBUTION = (() => {
    const storageKey = 'blend:first-touch:v2';
    const idleLimit = 30 * 60 * 1000;
    const writeInterval = 15 * 1000;
    const campaignKeys = ['utm_source', 'utm_medium', 'utm_campaign'];
    const entryKeys = [...campaignKeys, 'first_touch_url'];
    let record = null;
    let lastWrittenAt = -Infinity;

    const currentEntry = () => {
      const url = new URL(window.location.href);
      url.hash = '';
      return {
        ...Object.fromEntries(campaignKeys.map((key) => [key, url.searchParams.get(key) || ''])),
        first_touch_url: url.toString(),
      };
    };

    try {
      const stored = window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true
        ? JSON.parse(window.sessionStorage.getItem(storageKey) || 'null') : null;
      const now = Date.now();
      if (
        stored?.version === 2 &&
        entryKeys.every((key) => typeof stored.entry?.[key] === 'string') &&
        Number.isFinite(stored.entered_at) && Number.isFinite(stored.last_activity_at) &&
        stored.entered_at <= stored.last_activity_at && stored.last_activity_at <= now &&
        new URL(stored.entry.first_touch_url).origin === new URL(window.location.href).origin
      ) record = stored;
    } catch (_error) {
      // Invalid or unavailable storage falls back to this page's actual entry.
    }

    const persist = (force = false) => {
      const now = Date.now();
      if (window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() !== true) return;
      if (!record || (!force && now - lastWrittenAt < writeInterval)) return;
      lastWrittenAt = now;
      try {
        window.sessionStorage.setItem(storageKey, JSON.stringify(record));
      } catch (_error) {
        // Keep the in-memory visit when browser storage is unavailable.
      }
    };

    const touch = () => {
      const now = Date.now();
      const fresh = currentEntry();
      const hasCampaign = campaignKeys.some((key) => fresh[key].trim() !== '');
      const newCampaign = record && hasCampaign && campaignKeys.some((key) => fresh[key] !== record.entry[key]);
      const newVisit = !record || now - record.last_activity_at >= idleLimit || newCampaign;
      if (newVisit) {
        record = { version: 2, entry: fresh, entered_at: now, last_activity_at: now };
      } else {
        record.last_activity_at = now;
      }
      persist(newVisit);
      return record.entry;
    };

    // Capture before UI setup; navigation and reload are visit activity too.
    touch();
    window.addEventListener('pagehide', () => persist(true));
    return { touch, current: () => record.entry };
  })();

  const SCHEDULER_FALLBACK_TIMEZONES = [
    'Australia/Brisbane',
    'Australia/Sydney',
    'Australia/Melbourne',
    'Europe/London',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Asia/Singapore',
    'Asia/Dubai',
    'Pacific/Auckland',
  ];

  const schedulerDateParts = (value, timeZone) =>
    Object.fromEntries(
      new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
        .formatToParts(value)
        .filter((part) => part.type !== 'literal')
        .map((part) => [part.type, part.value])
    );

  const schedulerDateKey = (value, timeZone) => {
    const parts = schedulerDateParts(value, timeZone);
    return `${parts.year}-${parts.month}-${parts.day}`;
  };

  class AgencyContactDrawer extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.form = this.querySelector('[data-agency-contact-form]');
      this.captureFirstTouch();
      ['pointerdown', 'keydown', 'input', 'change'].forEach((type) => {
        this.addEventListener(type, (event) => {
          if (event.isTrusted) VISIT_ATTRIBUTION.touch();
          if (event.isTrusted && (type === 'input' || type === 'change')) this.measure('fieldInteraction', event);
        }, { signal: this.signal, passive: true });
      });
      this.confirmation = this.querySelector('[data-contact-confirmation]');
      this.setupContactDrawer();
      this.setupContactForm();
      this.renderWebMcpDocuments();
      this.setupDrawerRoutes();
      this.setupBookingPrewarm();
      try {
        this.measurement = window.BlendContactMeasurement ? new window.BlendContactMeasurement(this) : null;
      } catch (_error) { /* Analytics must never prevent the contact drawer from working. */ }
      document.addEventListener('shopify:section:select', (event) => {
        if (!event.target.contains(this)) return;
        this.openContactDrawer(null, false);
      }, { signal: this.signal });
      document.addEventListener('shopify:block:select', (event) => {
        if (!this.contains(event.target)) return;
        this.setDrawerRoute('form', { animate: false, focus: false });
        this.openContactDrawer(null, false);
      }, { signal: this.signal });
    }

    disconnectedCallback() {
      this.measure('destroy');
      this.cancelPendingDrawerOpen?.();
      this.cancelDrawerStyles?.();
      this.styleErrorDialog?.close?.();
      this.abortController?.abort();
      this.webMcpDocuments?.forEach((document) => {
        if (document.url) URL.revokeObjectURL(document.url);
        document.url = null;
      });
      this.cancelBookingPrewarmWork();
      this.bookingPrewarmResolve?.(false);
      this.bookingPrewarmResolve = null;
      this.bookingPrewarmPromise = null;
      window.clearTimeout(this.autoAdvanceTimer);
      window.clearTimeout(this.drawerOpenTimer);
      window.clearTimeout(this.drawerCloseTimer);
      window.clearTimeout(this.questionTypingTimer);
      this.drawerOpenCleanup?.();
      this.drawerCloseCleanup?.();
      this.motionFinishers?.forEach((finish) => finish());
      this.motionFinishers?.clear();
      this.composerMotion?.cancel();

      document
        .querySelectorAll(`[aria-controls="${this.drawer?.id || ''}"]`)
        .forEach((trigger) => {
          trigger.setAttribute('aria-expanded', 'false');
          trigger.removeAttribute('aria-controls');
        });

      if (this.drawer?.open) this.drawer.close();
      this.unlockDrawerScroll(false);
      document.dispatchEvent(new CustomEvent('blend:contact-drawer-state', { detail: { open: false } }));
    }

    setupContactDrawer() {
      this.drawer = this.querySelector('[data-contact-drawer]');
      if (!this.drawer) return;

      this.drawerPanel = this.drawer.querySelector('[data-contact-drawer-panel]');
      this.drawerTriggers = Array.from(this.querySelectorAll('[data-contact-drawer-open]'));
      this.drawerCloseButtons = Array.from(this.drawer.querySelectorAll('[data-contact-drawer-close]'));
      this.drawerClosing = false;
      this.drawerMotionId = 0;
      this.drawerState = 'closed';
      this.styleErrorDialog = this.querySelector('[data-contact-style-error]');
      this.styleErrorDialog?.querySelector('[data-contact-style-error-close]')?.addEventListener('click', () => {
        if (typeof this.styleErrorDialog.close === 'function') this.styleErrorDialog.close();
        else this.styleErrorDialog.removeAttribute('open');
      }, { signal: this.signal });

      this.refreshDrawerTriggers(document);
      document.addEventListener('shopify:section:load', (event) => this.refreshDrawerTriggers(event.target), { signal: this.signal });
      document.addEventListener('click', (event) => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const trigger = event.target instanceof Element ? event.target.closest('a[href], [data-contact-drawer-open], [onclick*="calendly.com"]') : null;
        if (!this.isDrawerTrigger(trigger)) return;
        if (this.contains(trigger)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        let returnTarget = trigger;
        const menu = trigger.closest('header-drawer');
        if (menu) {
          const summary = menu.querySelector('details > summary');
          menu.closeMenuDrawer?.(event, summary);
          returnTarget = summary || trigger;
        }
        const route = this.drawerRouteForTrigger(trigger);
        if (trigger.closest('.agency-contact-landing')) this.measure('landingSelect', route);
        this.setDrawerRoute(route, { animate: false, focus: false });
        this.openContactDrawer(trigger, true, event.detail === 0, returnTarget);
      }, { signal: this.signal, capture: true });
      this.drawerCloseButtons.forEach((button) => {
        button.addEventListener('click', () => this.closeContactDrawer(true, 'close_button'), { signal: this.signal });
      });
      this.drawer.addEventListener(
        'click',
        (event) => {
          if (event.target === this.drawer) this.closeContactDrawer(true, 'backdrop');
        },
        { signal: this.signal }
      );
      this.drawer.addEventListener(
        'cancel',
        (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.closeContactDrawer(true, 'escape');
        },
        { signal: this.signal }
      );
      this.drawer.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'Escape' && event.code !== 'Escape') return;
          event.preventDefault();
          event.stopPropagation();
          this.closeContactDrawer(true, 'escape');
        },
        { signal: this.signal }
      );

      if (this.querySelector('[data-shopify-contact-success], .agency-contact-hero__errors')) {
        requestAnimationFrame(() => this.openContactDrawer(null, false));
      }
    }

    drawerRouteForTrigger(trigger) {
      if (!(trigger instanceof HTMLElement) || trigger.hasAttribute('download')) return null;
      const explicitRoute = ['start', 'form', 'calendar'].includes(trigger.dataset.contactDrawerRoute)
        ? trigger.dataset.contactDrawerRoute : null;
      const inlineBooking = trigger.getAttribute('onclick')?.match(/https?:\/\/(?:www\.)?calendly\.com\/[^\s"']+/i)?.[0];
      if (trigger instanceof HTMLAnchorElement && trigger.getAttribute('href')?.startsWith('#') && !inlineBooking && !trigger.hasAttribute('data-contact-drawer-open')) return null;
      const href = trigger instanceof HTMLAnchorElement ? trigger.href : inlineBooking;
      if (href) {
        try {
          const url = new URL(inlineBooking || href, location.href);
          if (!['http:', 'https:'].includes(url.protocol)) return null;
          if (['calendly.com', 'www.calendly.com'].includes(url.hostname)) return 'calendar';
          const internal = url.origin === location.origin || ['blendcommerce.com', 'www.blendcommerce.com', 'gripson-e-commerce.myshopify.com'].includes(url.hostname);
          if (internal && /(?:^|\/)pages\/contact-us\/?$/i.test(url.pathname)) return explicitRoute || 'start';
          if (internal && /(?:^|\/)pages\/book-a-call\/?$/i.test(url.pathname)) return 'calendar';
        } catch (_error) { /* Explicit drawer buttons can work without a valid URL. */ }
      }
      return trigger.hasAttribute('data-contact-drawer-open') ? explicitRoute || 'start' : null;
    }

    isDrawerTrigger(trigger) {
      return this.drawerRouteForTrigger(trigger) !== null;
    }

    refreshDrawerTriggers(root) {
      root.querySelectorAll?.('a[href], [data-contact-drawer-open], [onclick*="calendly.com"]').forEach((trigger) => {
        if (!this.isDrawerTrigger(trigger) || this.contains(trigger)) return;
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-controls', this.drawer.id);
        trigger.setAttribute('aria-expanded', String(this.drawer.open));
      });
    }

    ensureDrawerStyles() {
      const link = document.querySelector('[data-contact-drawer-styles]');
      if (!link) return Promise.resolve(getComputedStyle(this.drawer).position === 'fixed');
      if (link.dataset.loadState === 'loaded' && link.sheet) {
        link.media = 'all';
        return Promise.resolve(true);
      }
      if (link.dataset.loadState === 'error') return Promise.resolve(false);
      if (this.drawerStylesPromise) return this.drawerStylesPromise;
      this.drawerStylesPromise = new Promise((resolve) => {
        let settled = false;
        const finish = (loaded) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          link.removeEventListener('load', onLoad);
          link.removeEventListener('error', onError);
          this.cancelDrawerStyles = null;
          if (loaded) link.media = 'all';
          resolve(loaded);
        };
        const onLoad = () => {
          link.dataset.loadState = 'loaded';
          finish(true);
        };
        const onError = () => finish(false);
        const timer = setTimeout(() => finish(false), 8000);
        this.cancelDrawerStyles = () => finish(false);
        link.addEventListener('load', onLoad, { once: true });
        link.addEventListener('error', onError, { once: true });
        link.media = 'all';
        if (link.dataset.loadState === 'loaded' && link.sheet) finish(true);
      });
      return this.drawerStylesPromise;
    }

    openContactDrawer(trigger = null, trackOpen = true, openedWithKeyboard = false, returnFocusTarget = trigger) {
      if (!this.drawer || this.drawer.open || this.drawerState !== 'closed') return Promise.resolve(false);
      this.pendingDrawerOpen = [trigger, trackOpen, openedWithKeyboard, returnFocusTarget];
      if (this.drawerOpenRequest) return this.drawerOpenRequest;
      const cancelled = new Promise((resolve) => {
        this.cancelPendingDrawerOpen = () => resolve(null);
      });
      const cancelOnEscape = (event) => {
        if (event.key !== 'Escape' && event.code !== 'Escape') return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.cancelPendingDrawerOpen?.();
      };
      document.addEventListener('keydown', cancelOnEscape, true);
      this.drawerOpenRequest = Promise.race([this.ensureDrawerStyles(), cancelled]).then((loaded) => {
        if (loaded === null || !this.isConnected) return false;
        if (!loaded) {
          if (this.styleErrorDialog && !this.styleErrorDialog.open) {
            if (typeof this.styleErrorDialog.showModal === 'function') this.styleErrorDialog.showModal();
            else this.styleErrorDialog.setAttribute('open', '');
          }
          return false;
        }
        this.styleErrorDialog?.close?.();
        this.showContactDrawer(...this.pendingDrawerOpen);
        return Boolean(this.drawer.open);
      }).finally(() => {
        document.removeEventListener('keydown', cancelOnEscape, true);
        this.cancelPendingDrawerOpen = null;
        this.drawerOpenRequest = null;
        this.pendingDrawerOpen = null;
        this.pendingMeasurementEntry = null;
      });
      return this.drawerOpenRequest;
    }

    showContactDrawer(trigger = null, trackOpen = true, openedWithKeyboard = false, returnFocusTarget = trigger) {
      if (!this.drawer || this.drawer.open || this.drawerState !== 'closed') return;

      const motionId = ++this.drawerMotionId;
      this.drawerState = 'opening';
      this.activeDrawerTrigger = trigger instanceof HTMLElement ? trigger : null;
      this.drawerReturnFocusTarget = returnFocusTarget instanceof HTMLElement ? returnFocusTarget : null;
      this.activeDrawerTrigger?.setAttribute('aria-controls', this.drawer.id);
      this.activeDrawerTrigger?.setAttribute('aria-expanded', 'true');
      this.drawerTriggers = Array.from(document.querySelectorAll(`[aria-controls="${this.drawer.id}"]`)).filter((trigger) => !this.contains(trigger));
      this.drawerTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
      this.lockDrawerScroll();
      document.dispatchEvent(new CustomEvent('blend:contact-drawer-state', { detail: { open: true } }));
      window.clearTimeout(this.drawerOpenTimer);
      window.clearTimeout(this.drawerCloseTimer);
      this.drawerOpenCleanup?.();
      this.drawerCloseCleanup?.();
      this.drawer.classList.remove('is-preparing', 'is-opening', 'is-open', 'is-closing');
      this.drawer.classList.add('is-preparing');

      if (typeof this.drawer.showModal === 'function') {
        this.drawer.showModal();
        this.drawerUsesFallback = false;
      } else {
        this.drawer.setAttribute('open', '');
        this.drawerUsesFallback = true;
      }

      // Native dialog focus otherwise scrolls the transformed panel into view and visually cancels the slide.
      this.drawer.focus({ preventScroll: true });
      this.drawer.scrollLeft = 0;

      const focusDrawer = () => {
        if (motionId !== this.drawerMotionId || this.drawerState === 'closing') return;

        const returnedState = this.drawer.querySelector(
          '.agency-contact-hero__status, .agency-contact-hero__errors, .agency-contact-hero__result:not([hidden])'
        );
        const activeStep = this.steps?.[this.currentStep];
        const activeTextField = activeStep?.querySelector(
          'input:not([type="hidden"]):not([type="radio"]):not([type="checkbox"]):not([type="file"]):not([disabled]), textarea:not([disabled])'
        );
        const routeFocusTarget =
          this.drawerRoute === 'start'
            ? (openedWithKeyboard ? this.drawerRouteButtons?.find((button) => button.dataset.drawerRouteSelect === 'form') : this.drawerStartHeading)
            : this.drawerRoute === 'calendar'
              ? this.calendlyTitle
              : this.transcript?.querySelector('[data-current-typewriter]');
        const focusTarget = returnedState || routeFocusTarget || this.drawerPanel;

        if (this.drawerUsesFallback && typeof trapFocus === 'function') trapFocus(this.drawerPanel, focusTarget);
        else focusTarget?.focus({ preventScroll: true });
        if (this.stepAnnouncer && !returnedState) {
          const announcement = this.stepAnnouncer.textContent;
          this.stepAnnouncer.textContent = '';
          requestAnimationFrame(() => {
            this.stepAnnouncer.textContent = announcement;
          });
        }
        if (this.drawerRoute === 'form') {
          this.startCurrentQuestionTyping(openedWithKeyboard && !returnedState && Boolean(activeTextField));
        }
      };

      if (this.reducedMotionQuery.matches || !this.drawerPanel) {
        this.drawer.classList.remove('is-preparing');
        this.drawer.classList.add('is-open');
        this.drawerState = 'open';
        requestAnimationFrame(focusDrawer);
      } else {
        const cleanupOpening = () => {
          this.drawerPanel.removeEventListener('transitionend', handleOpeningEnd);
          if (this.drawerOpenCleanup === cleanupOpening) this.drawerOpenCleanup = null;
        };
        const finishOpening = () => {
          if (motionId !== this.drawerMotionId || this.drawerState !== 'opening') return;

          window.clearTimeout(this.drawerOpenTimer);
          cleanupOpening();
          this.drawer.classList.remove('is-preparing', 'is-opening');
          this.drawer.classList.add('is-open');
          this.drawerState = 'open';
          requestAnimationFrame(focusDrawer);
        };
        const handleOpeningEnd = (event) => {
          if (event.target !== this.drawerPanel || event.propertyName !== 'transform') return;
          finishOpening();
        };

        this.drawerOpenCleanup = cleanupOpening;
        this.drawerPanel.addEventListener('transitionend', handleOpeningEnd);
        this.drawerPanel.getBoundingClientRect();
        this.drawer.classList.remove('is-preparing');
        this.drawer.classList.add('is-opening');
        this.drawerOpenTimer = window.setTimeout(finishOpening, 900);
      }

      this.measure('open', this.pendingMeasurementEntry || trigger);
      this.pendingMeasurementEntry = null;
      if (trackOpen) this.track('agency_contact_drawer_open');
    }

    closeContactDrawer(restoreFocus = true, reason = 'programmatic') {
      if (!this.drawer?.open || this.drawerClosing) return;

      this.measure('close', reason);

      const motionId = ++this.drawerMotionId;
      this.drawerClosing = true;
      this.drawerState = 'closing';
      window.clearTimeout(this.drawerOpenTimer);
      window.clearTimeout(this.drawerCloseTimer);
      window.clearTimeout(this.questionTypingTimer);
      window.clearTimeout(this.autoAdvanceTimer);
      this.drawerOpenCleanup?.();

      const finishClose = () => {
        if (motionId !== this.drawerMotionId) return;

        window.clearTimeout(this.drawerCloseTimer);
        cleanupClosing?.();
        this.drawer.classList.remove('is-preparing', 'is-opening', 'is-open', 'is-closing');
        if (this.drawer.open) this.drawer.close();
        if (this.drawerUsesFallback && typeof removeTrapFocus === 'function') removeTrapFocus();
        this.drawerTriggers?.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
        this.unlockDrawerScroll();
        this.drawerClosing = false;
        this.drawerState = 'closed';
        this.setDrawerRoute?.('start', { animate: false, focus: false });
        document.dispatchEvent(new CustomEvent('blend:contact-drawer-state', { detail: { open: false } }));

        this.activeDrawerTrigger?.setAttribute('aria-expanded', 'false');
        if (restoreFocus) this.drawerReturnFocusTarget?.focus({ preventScroll: true });
        this.drawerReturnFocusTarget = null;
        this.activeDrawerTrigger = null;
      };

      let cleanupClosing = null;

      if (this.reducedMotionQuery.matches || !this.drawerPanel) {
        finishClose();
        return;
      }

      const handleClosingEnd = (event) => {
        if (event.target !== this.drawerPanel || event.propertyName !== 'transform') return;
        finishClose();
      };
      cleanupClosing = () => {
        this.drawerPanel.removeEventListener('transitionend', handleClosingEnd);
        if (this.drawerCloseCleanup === cleanupClosing) this.drawerCloseCleanup = null;
      };
      this.drawerCloseCleanup = cleanupClosing;
      this.drawerPanel.addEventListener('transitionend', handleClosingEnd);
      this.drawer.classList.remove('is-preparing', 'is-opening', 'is-open');
      this.drawer.classList.add('is-closing');
      this.drawerCloseTimer = window.setTimeout(() => {
        finishClose();
      }, 900);
    }

    setupBookingPrewarm() {
      this.bookingFrame = this.drawer?.querySelector('.agency-contact-hero__calendly-frame');
      if (!this.bookingFrame) return;

      const originalUrl = this.bookingFrame.dataset.bookingSrc;
      const attributedUrl = this.bookingUrlWithAttribution(originalUrl);
      if (attributedUrl !== originalUrl) {
        this.bookingFrame.dataset.bookingSrc = attributedUrl;
        delete this.bookingFrame.dataset.bookingPrewarmState;
      }
      this.bookingFrameReady = this.bookingFrame.dataset.bookingPrewarmState === 'loaded';
      this.bookingFrame.addEventListener('load', () => {
        this.drawer.querySelector('[data-calendly-loading]')?.setAttribute('hidden', '');
        this.bookingFrameReady = true;
        this.bookingFrame.dataset.bookingPrewarmState = 'loaded';
        this.measure('bookingFrameLoaded');
        this.cancelBookingPrewarmWork();
        this.bookingPrewarmResolve?.(true);
        this.bookingPrewarmResolve = null;
      }, { signal: this.signal });
      this.bookingFrame.addEventListener('error', () => {
        this.measure('bookingFrameError');
        const loading = this.drawer.querySelector('[data-calendly-loading]');
        if (loading) loading.textContent = 'Calendar unavailable. Use the booking link below.';
        this.bookingPrewarmResolve?.(false);
        this.bookingPrewarmResolve = null;
        this.bookingPrewarmPromise = null;
      }, { signal: this.signal });


    }

    bookingUrlWithAttribution(value) {
      try {
        const url = new URL(value);
        if (url.protocol !== 'https:' || !['calendly.com', 'www.calendly.com'].includes(url.hostname)) return value;
        url.searchParams.set('embed_domain', window.location.host);
        url.searchParams.set('embed_type', 'Inline');
        url.searchParams.set('hide_event_type_details', '1');
        url.searchParams.delete('hide_gdpr_banner');
        const styles = getComputedStyle(this);
        [['background_color', '--agency-hero-panel'], ['text_color', '--agency-hero-text'], ['primary_color', '--agency-hero-accent']].forEach(([parameter, property]) => {
          const color = styles.getPropertyValue(property).trim().replace(/^#/, '');
          if (/^[0-9a-f]{6}$/i.test(color)) url.searchParams.set(parameter, color);
        });
        const attribution = VISIT_ATTRIBUTION.current();
        ['utm_source', 'utm_medium', 'utm_campaign'].forEach((key) => {
          if (attribution[key]) url.searchParams.set(key, attribution[key]);
          else url.searchParams.delete(key);
        });
        return url.toString();
      } catch (_error) {
        return value;
      }
    }

    canPrewarmBookingFrame() {
      return Boolean(
        this.isConnected &&
        document.readyState === 'complete' &&
        !document.hidden &&
        navigator.onLine !== false &&
        !navigator.connection?.saveData &&
        !window.Shopify?.designMode
      );
    }

    cancelBookingPrewarmWork() {
      window.clearTimeout(this.bookingPrewarmTimer);
      this.bookingPrewarmTimer = null;
      if (this.bookingPrewarmIdle != null) window.cancelIdleCallback?.(this.bookingPrewarmIdle);
      this.bookingPrewarmIdle = null;
    }

    scheduleBookingPrewarm() {
      if (!this.bookingFrame || this.bookingPrewarmPromise || this.bookingFrameReady) return;
      if (!this.canPrewarmBookingFrame()) {
        this.cancelBookingPrewarmWork();
        return;
      }
      if (this.bookingPrewarmTimer != null || this.bookingPrewarmIdle != null) return;

      // Keep speculative third-party work outside the initial load and its busy period.
      this.bookingPrewarmTimer = window.setTimeout(() => {
        this.bookingPrewarmTimer = null;
        if (!this.canPrewarmBookingFrame()) return;
        if (typeof window.requestIdleCallback === 'function') {
          this.bookingPrewarmIdle = window.requestIdleCallback(() => {
            this.bookingPrewarmIdle = null;
            this.prewarmBookingFrame();
          });
        } else {
          this.prewarmBookingFrame();
        }
      }, 5000);
    }

    prewarmBookingFrame({ intent = false } = {}) {
      if (!this.bookingFrame || !this.isConnected) return Promise.resolve(false);
      if (!intent) return Promise.resolve(false);
      if (this.bookingPrewarmPromise) return this.bookingPrewarmPromise;

      this.cancelBookingPrewarmWork();
      this.bookingPrewarmPromise = new Promise((resolve) => {
        this.bookingPrewarmResolve = resolve;
      });
      if (this.bookingFrameReady) {
        this.bookingPrewarmResolve(true);
        this.bookingPrewarmResolve = null;
      } else {
        this.bookingFrame.dataset.bookingPrewarmState = 'loading';
        // Reuse the existing frame and URL; never create a second embed or open the drawer.
        this.bookingFrame.loading = 'eager';
        this.bookingFrame.src = this.bookingFrame.dataset.bookingSrc;
      }
      return this.bookingPrewarmPromise;
    }

    setupDrawerRoutes() {
      this.drawerRouter = this.drawer?.querySelector('[data-contact-drawer-router]');
      if (!this.drawerRouter) return;

      this.drawerRoutePanels = new Map(
        Array.from(this.drawerRouter.querySelectorAll('[data-drawer-route-panel]')).map((panel) => [
          panel.dataset.drawerRoutePanel,
          panel,
        ])
      );
      this.drawerRouteButtons = Array.from(this.drawerRouter.querySelectorAll('[data-drawer-route-select]'));
      this.drawerRouteBackButtons = Array.from(this.drawer.querySelectorAll('[data-drawer-route-back]'));
      this.drawerForwardButton = this.drawer.querySelector('[data-drawer-forward]');
      this.drawerResetButton = this.drawer.querySelector('[data-drawer-reset]');
      this.drawerForwardRoute = '';
      this.schedulerResultCtas = Array.from(this.drawerRouter.querySelectorAll('[data-open-native-scheduler]'));
      this.drawerProgress = this.drawer.querySelector('[data-drawer-progress]');
      this.drawerStartHeading = this.drawerRouter.querySelector('[data-drawer-start-heading]');
      this.scheduler = this.drawerRouter.querySelector('[data-native-scheduler]');
      this.schedulerLoading = this.drawerRouter.querySelector('[data-scheduler-loading]');
      this.schedulerError = this.drawerRouter.querySelector('[data-scheduler-error]');
      this.schedulerErrorMessage = this.drawerRouter.querySelector('[data-scheduler-error-message]');
      this.schedulerRetry = this.drawerRouter.querySelector('[data-scheduler-retry]');
      this.schedulerInterface = this.drawerRouter.querySelector('[data-scheduler-interface]');
      this.schedulerPicker = this.drawerRouter.querySelector('[data-scheduler-picker]');
      this.schedulerStatus = this.drawerRouter.querySelector('[data-scheduler-status]');
      this.schedulerMonthLabel = this.drawerRouter.querySelector('[data-scheduler-month-label]');
      this.schedulerPreviousMonth = this.drawerRouter.querySelector('[data-scheduler-previous-month]');
      this.schedulerNextMonth = this.drawerRouter.querySelector('[data-scheduler-next-month]');
      this.schedulerCalendar = this.drawerRouter.querySelector('[data-scheduler-calendar]');
      this.schedulerTimezoneSelect = this.drawerRouter.querySelector('[data-scheduler-timezone]');
      this.schedulerTimesPanel = this.drawerRouter.querySelector('[data-scheduler-times-panel]');
      this.schedulerTimesHeading = this.drawerRouter.querySelector('[data-scheduler-times-heading]');
      this.schedulerSelectedDate = this.drawerRouter.querySelector('[data-scheduler-selected-date]');
      this.schedulerTimes = this.drawerRouter.querySelector('[data-scheduler-times]');
      this.schedulerChangeDate = this.drawerRouter.querySelector('[data-scheduler-change-date]');
      this.schedulerBookingForm = this.drawerRouter.querySelector('[data-scheduler-booking-form]');
      this.schedulerSelectedTime = this.drawerRouter.querySelector('[data-scheduler-selected-time]');
      this.schedulerChangeTime = this.drawerRouter.querySelector('[data-scheduler-change-time]');
      this.schedulerName = this.drawerRouter.querySelector('[data-scheduler-name]');
      this.schedulerEmail = this.drawerRouter.querySelector('[data-scheduler-email]');
      this.schedulerFormError = this.drawerRouter.querySelector('[data-scheduler-form-error]');
      this.schedulerSubmit = this.drawerRouter.querySelector('[data-scheduler-submit]');
      this.schedulerSubmitLabel = this.drawerRouter.querySelector('[data-scheduler-submit-label]');
      this.schedulerConfirmation = this.drawerRouter.querySelector('[data-scheduler-confirmation]');
      this.schedulerConfirmationTime = this.drawerRouter.querySelector('[data-scheduler-confirmation-time]');
      this.schedulerReschedule = this.drawerRouter.querySelector('[data-scheduler-reschedule]');
      this.schedulerCancel = this.drawerRouter.querySelector('[data-scheduler-cancel]');
      this.schedulerFallback = this.drawerRouter.querySelector('[data-scheduler-fallback]');
      this.calendlyTitle = this.drawer.querySelector('.agency-contact-hero__drawer-title');
      this.schedulerInitialized = false;
      this.schedulerLoadingPromise = null;
      this.schedulerSlots = [];
      this.schedulerMonths = [];
      this.schedulerSelectedDateKey = '';
      this.schedulerSelectedSlot = '';
      this.schedulerTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Australia/Brisbane';
      this.drawerRoute = null;

      this.drawerRouteButtons.forEach((button) => {
        button.addEventListener(
          'click',
          (event) => {
            this.measure('event', 'contact_route_select', { route: button.dataset.drawerRouteSelect, action: 'route_select' });
            this.setDrawerRoute(button.dataset.drawerRouteSelect, {
              focus: true,
              focusComposer: event.detail === 0,
            });
          },
          { signal: this.signal }
        );
      });
      this.drawerRouteBackButtons.forEach((button) => {
        button.addEventListener('click', () => this.navigateDrawerBack(), { signal: this.signal });
      });
      this.drawerForwardButton?.addEventListener('click', () => this.navigateDrawerForward(), { signal: this.signal });
      this.drawerResetButton?.addEventListener('click', () => this.resetContactForm(), { signal: this.signal });
      this.schedulerResultCtas.forEach((link) => {
        link.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            this.measure('event', 'contact_route_select', { route: 'calendar', action: 'success' });
            this.setDrawerRoute('calendar', { focus: true });
          },
          { signal: this.signal }
        );
      });
      this.schedulerRetry?.addEventListener('click', () => {
        this.measure('event', 'contact_booking_action', { action: 'retry' });
        this.initializeScheduler(true);
      }, { signal: this.signal });
      this.schedulerPreviousMonth?.addEventListener('click', () => this.changeSchedulerMonth(-1), {
        signal: this.signal,
      });
      this.schedulerNextMonth?.addEventListener('click', () => this.changeSchedulerMonth(1), {
        signal: this.signal,
      });
      this.schedulerTimezoneSelect?.addEventListener('change', () => this.changeSchedulerTimezone(), {
        signal: this.signal,
      });
      this.schedulerChangeDate?.addEventListener('click', () => {
        this.measure('event', 'contact_booking_action', { action: 'change_date' });
        this.showSchedulerPicker();
      }, { signal: this.signal });
      this.schedulerChangeTime?.addEventListener('click', () => {
        this.measure('event', 'contact_booking_action', { action: 'change_time' });
        this.showSchedulerTimes();
      }, { signal: this.signal });
      this.schedulerBookingForm?.addEventListener('invalid', (event) => this.measure('validation', event.target, -1), { signal: this.signal, capture: true });
      [[this.schedulerFallback, 'fallback'], [this.schedulerReschedule, 'reschedule'], [this.schedulerCancel, 'calendar_cancel']].forEach(([link, action]) => {
        link?.addEventListener('click', () => this.measure('event', 'contact_booking_action', { action }), { signal: this.signal });
      });
      this.schedulerBookingForm?.addEventListener('submit', (event) => this.submitSchedulerBooking(event), {
        signal: this.signal,
      });

      const returnedFormState = this.drawer.querySelector(
        '.agency-contact-hero__status, .agency-contact-hero__errors, .agency-contact-hero__result:not([hidden])'
      );
      this.setDrawerRoute(returnedFormState ? 'form' : 'start', { animate: false, focus: false });
    }

    drawerNavigationBusy() {
      return this.isTransitioning || this.form?.dataset.submitting === 'true' || this.form?.dataset.finalizing === 'true';
    }

    syncDrawerNavigation() {
      const busy = this.drawerNavigationBusy();
      const hasForm = Boolean(this.form?.isConnected && this.form.querySelector('[data-form-step]'));
      this.drawerRouteBackButtons?.forEach((button) => {
        button.disabled = busy || this.drawerRoute === 'start';
      });
      if (this.drawerForwardButton) {
        const canForward = this.drawerRoute === 'start'
          ? Boolean(this.drawerForwardRoute)
          : this.drawerRoute === 'form' && hasForm && this.currentStep < this.furthestStep;
        this.drawerForwardButton.disabled = busy || !canForward;
      }
      if (this.drawerResetButton) {
        this.drawerResetButton.hidden = this.drawerRoute !== 'form' || !hasForm;
        this.drawerResetButton.disabled = busy;
      }
    }

    navigateDrawerBack() {
      if (this.drawerNavigationBusy() || this.drawerRoute === 'start') return;
      if (this.inlineEdit && !this.finishInlineEdit(true, true)) return;
      this.measure('event', 'contact_navigation', { action: 'back' });
      this.finishCurrentQuestionTyping(false);
      if (this.drawerRoute === 'form' && this.currentStep > 0 && this.form?.isConnected) {
        this.transitionToStep(this.currentStep - 1, true, 'back');
      } else {
        this.drawerForwardRoute = this.drawerRoute;
        this.setDrawerRoute('start', { focus: true, returnRoute: this.drawerRoute, preserveHistory: true });
      }
    }

    navigateDrawerForward() {
      if (this.drawerNavigationBusy()) return;
      if (this.inlineEdit && !this.finishInlineEdit(true, true)) return;
      this.measure('event', 'contact_navigation', { action: 'forward' });
      if (this.drawerRoute === 'start' && this.drawerForwardRoute) {
        this.setDrawerRoute(this.drawerForwardRoute, { focus: true, preserveHistory: true });
      } else if (this.drawerRoute === 'form' && this.currentStep < this.furthestStep) {
        this.goToStep(this.currentStep + 1, true);
      }
    }

    resetContactForm() {
      if (this.drawerNavigationBusy() || !this.steps?.length || !this.form?.isConnected) return;
      this.measure('reset');
      if (this.inlineEdit) this.finishInlineEdit(false);
      window.clearTimeout(this.autoAdvanceTimer);
      window.clearTimeout(this.questionTypingTimer);
      this.form.reset();
      this.clearWebMcpDocuments();
      this.submissionId = null;
      this.webMcpReview = null;
      this.webMcpSubmission = null;
      this.steps.forEach((step) => {
        step.querySelectorAll('input:not([type="hidden"]), textarea, select').forEach((field) => {
          if (field.type === 'radio' || field.type === 'checkbox') field.checked = false;
          else field.value = '';
          field.setCustomValidity('');
          this.clearClientInvalid(field);
        });
      });
      this.furthestStep = 0;
      this.drawerForwardRoute = '';
      this.latestAnswerStep = null;
      this.presentedQuestionSteps.clear();
      delete this.form.dataset.finalizing;
      this.setSubmitting(false);
      this.syncAllOtherChoices();
      this.syncAllMultiChoices();
      this.updateDerivedFields();
      this.captureFirstTouch();
      this.resizeMessageComposer();
      if (this.fileName) {
        this.fileName.textContent = this.fileNameDefault;
        this.fileName.classList.remove('has-file');
      }
      const removeFilesButton = this.form.querySelector('[data-files-remove]');
      if (removeFilesButton) removeFilesButton.hidden = true;
      this.pendingResetAnnouncement = 'Form reset';
      this.showStep(0, false);
      this.setDrawerRoute('form', { animate: false, focus: true, focusComposer: true });
    }

    setDrawerRoute(route, options = {}) {
      if (!this.drawerRouter || !this.drawerRoutePanels?.has(route)) return;

      const { animate = true, focus = false, focusComposer = false, returnRoute = '' } = options;
      const previousRoute = this.drawerRoute;
      if (previousRoute === 'form' && route !== 'form' && this.form?.dataset.questionTyping === 'true') {
        this.finishCurrentQuestionTyping(false);
      }
      if (route !== 'form') window.clearTimeout(this.autoAdvanceTimer);

      this.drawerRoute = route;
      this.drawerRouter.dataset.drawerRoute = route;
      this.drawer.dataset.drawerRoute = route;
      if (this.calendlyTitle) {
        this.calendlyTitle.textContent = route === 'calendar' ? 'BOOK A CALL' : route === 'form' ? 'CONTACT FORM' : this.calendlyTitle.dataset.defaultTitle;
      }
      if (!options.preserveHistory) this.drawerForwardRoute = '';
      this.syncDrawerNavigation();
      if (this.drawerProgress) {
        this.drawerProgress.hidden = route !== 'form' || !this.form?.isConnected || !this.form.querySelector('[data-form-step]');
      }
      this.drawerRouteButtons.forEach((button) => {
        button.setAttribute('aria-expanded', String(button.dataset.drawerRouteSelect === route));
      });
      this.drawerRoutePanels.forEach((panel, panelRoute) => {
        const isActive = panelRoute === route;
        panel.hidden = !isActive;
        panel.inert = !isActive;
        panel.classList.remove('is-entering');
        if (isActive && animate && previousRoute && previousRoute !== route && !this.reducedMotionQuery.matches) {
          panel.classList.add('is-entering');
          panel.addEventListener('animationend', () => panel.classList.remove('is-entering'), { once: true });
        }
      });

      if (route === 'calendar') {
        this.prewarmBookingFrame({ intent: true });
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (this.drawerRoute === 'calendar') this.initializeScheduler();
          });
        });
      }

      this.measure('view', 'route_select');
      if (!focus || !this.drawer?.open || this.drawerState === 'closing') return;
      requestAnimationFrame(() => {
        if (!this.drawer?.open || this.drawerRoute !== route || this.drawerState === 'closing') return;

        if (route === 'start') {
          const targetRoute = returnRoute || 'form';
          const target = this.drawerRouteButtons.find((button) => button.dataset.drawerRouteSelect === targetRoute);
          (this.drawerStartHeading || target || this.drawerPanel)?.focus({ preventScroll: true });
          return;
        }

        if (route === 'calendar') {
          this.calendlyTitle?.focus({ preventScroll: true });
          return;
        }

        const returnedState = this.drawer.querySelector(
          '.agency-contact-hero__status, .agency-contact-hero__errors, .agency-contact-hero__result:not([hidden])'
        );
        if (returnedState) {
          returnedState.focus?.({ preventScroll: true });
          return;
        }

        const currentQuestion = this.transcript?.querySelector('[data-current-typewriter]');
        currentQuestion?.setAttribute('tabindex', '-1');
        currentQuestion?.focus({ preventScroll: true });
        this.startCurrentQuestionTyping(focusComposer);
      });
    }

    schedulerApiUrl(path) {
      try {
        const url = new URL(this.scheduler?.dataset.schedulerEndpoint || '');
        if (url.protocol !== 'https:') return null;
        url.hash = '';
        url.search = '';
        url.pathname = `${url.pathname.replace(/\/+$/, '')}${path}`;
        return url;
      } catch (_error) {
        return null;
      }
    }

    initializeScheduler(forceRetry = false) {
      if (!this.scheduler || this.drawerRoute !== 'calendar') return;
      if (this.schedulerInitialized && !forceRetry) {
        this.showSchedulerReady();
        return;
      }
      if (this.schedulerLoadingPromise) return;

      const endpoint = this.schedulerApiUrl('/calendly/availability');
      if (!endpoint) {
        this.showSchedulerError('Online booking is not configured yet. Please use the Calendly link below.');
        return;
      }

      this.populateSchedulerTimezones();
      this.showSchedulerLoading();
      const startTime = new Date(Date.now() + 60 * 1000);
      const endTime = new Date(startTime.getTime() + 31 * 24 * 60 * 60 * 1000);
      endpoint.searchParams.set('start_time', startTime.toISOString());
      endpoint.searchParams.set('end_time', endTime.toISOString());

      this.schedulerLoadingPromise = fetch(endpoint, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        credentials: 'omit',
        signal: this.signal,
      })
        .then(async (response) => {
          const payload = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(payload.error || 'We could not load the available times.');

          this.schedulerSlots = Array.isArray(payload.slots)
            ? payload.slots.filter((slot) => {
                const time = new Date(slot?.start_time);
                return !Number.isNaN(time.getTime()) && time.getTime() > Date.now();
              })
            : [];
          this.schedulerDuration = Number(payload.duration_minutes) || Number(this.scheduler.dataset.schedulerDuration) || 30;
          this.schedulerInitialized = true;
          this.schedulerSelectedDateKey = '';
          this.schedulerSelectedSlot = '';
          this.rebuildSchedulerMonths();
          this.renderSchedulerCalendar();
          this.showSchedulerPicker({ focus: false });
          this.showSchedulerReady();
          this.announceScheduler(
            this.schedulerSlots.length
              ? `${this.schedulerSlots.length} appointment times are available.`
              : 'There are no appointment times available in the next 31 days.'
          );
        })
        .catch((error) => {
          if (error.name !== 'AbortError') this.showSchedulerError(error.message);
        })
        .finally(() => {
          this.schedulerLoadingPromise = null;
        });
      return this.schedulerLoadingPromise;
    }

    populateSchedulerTimezones() {
      if (!this.schedulerTimezoneSelect || this.schedulerTimezoneSelect.options.length) return;

      let timezones = SCHEDULER_FALLBACK_TIMEZONES;
      if (typeof Intl.supportedValuesOf === 'function') {
        timezones = Intl.supportedValuesOf('timeZone');
      }
      const ordered = [this.schedulerTimezone, ...SCHEDULER_FALLBACK_TIMEZONES, ...timezones].filter(
        (timezone, index, values) => timezone && values.indexOf(timezone) === index
      );

      const fragment = document.createDocumentFragment();
      ordered.forEach((timezone) => {
        const option = document.createElement('option');
        option.value = timezone;
        option.textContent = `${timezone.replaceAll('_', ' ')} · ${this.schedulerTimezoneOffset(timezone)}`;
        option.selected = timezone === this.schedulerTimezone;
        fragment.append(option);
      });
      this.schedulerTimezoneSelect.append(fragment);
    }

    schedulerTimezoneOffset(timezone) {
      try {
        return (
          new Intl.DateTimeFormat('en-AU', {
            timeZone: timezone,
            timeZoneName: 'shortOffset',
          })
            .formatToParts(new Date())
            .find((part) => part.type === 'timeZoneName')?.value || timezone
        );
      } catch (_error) {
        return timezone;
      }
    }

    rebuildSchedulerMonths() {
      const todayMonth = schedulerDateKey(new Date(), this.schedulerTimezone).slice(0, 7);
      const monthSet = new Set([todayMonth]);
      this.schedulerSlots.forEach((slot) => {
        monthSet.add(schedulerDateKey(new Date(slot.start_time), this.schedulerTimezone).slice(0, 7));
      });
      this.schedulerMonths = Array.from(monthSet).sort();
      this.schedulerMonthIndex = Math.max(
        0,
        this.schedulerMonths.indexOf(this.schedulerDisplayedMonth || todayMonth)
      );
      this.schedulerDisplayedMonth = this.schedulerMonths[this.schedulerMonthIndex] || todayMonth;
    }

    renderSchedulerCalendar() {
      if (!this.schedulerCalendar || !this.schedulerDisplayedMonth) return;
      const [year, month] = this.schedulerDisplayedMonth.split('-').map(Number);
      const firstDay = new Date(Date.UTC(year, month - 1, 1));
      const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
      const leadingDays = (firstDay.getUTCDay() + 6) % 7;
      const todayKey = schedulerDateKey(new Date(), this.schedulerTimezone);
      const availableDates = new Map();

      this.schedulerSlots.forEach((slot) => {
        const key = schedulerDateKey(new Date(slot.start_time), this.schedulerTimezone);
        if (!availableDates.has(key)) availableDates.set(key, []);
        availableDates.get(key).push(slot);
      });
      const selectedDateIsAvailable = this.schedulerSelectedDateKey?.startsWith(`${this.schedulerDisplayedMonth}-`)
        && availableDates.has(this.schedulerSelectedDateKey);

      if (this.schedulerMonthLabel) {
        this.schedulerMonthLabel.textContent = new Intl.DateTimeFormat('en-AU', {
          month: 'long',
          year: 'numeric',
          timeZone: 'UTC',
        }).format(firstDay);
      }
      if (this.schedulerPreviousMonth) this.schedulerPreviousMonth.disabled = this.schedulerMonthIndex <= 0;
      if (this.schedulerNextMonth) {
        this.schedulerNextMonth.disabled = this.schedulerMonthIndex >= this.schedulerMonths.length - 1;
      }

      const fragment = document.createDocumentFragment();
      for (let index = 0; index < leadingDays; index += 1) {
        const spacer = document.createElement('span');
        spacer.className = 'agency-contact-hero__scheduler-date-spacer';
        spacer.setAttribute('aria-hidden', 'true');
        fragment.append(spacer);
      }

      let firstEnabled = true;
      for (let day = 1; day <= daysInMonth; day += 1) {
        const key = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const date = new Date(Date.UTC(year, month - 1, day, 12));
        const slots = availableDates.get(key) || [];
        const button = document.createElement('button');
        button.className = 'agency-contact-hero__scheduler-date';
        button.type = 'button';
        button.dataset.schedulerDate = key;
        button.setAttribute('aria-pressed', String(key === this.schedulerSelectedDateKey));
        button.setAttribute(
          'aria-label',
          `${new Intl.DateTimeFormat('en-AU', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            timeZone: 'UTC',
          }).format(date)}${
            slots.length
              ? `, ${slots.length} ${slots.length === 1 ? 'time' : 'times'} available`
              : ', unavailable'
          }`
        );
        button.textContent = String(day);
        button.disabled = slots.length === 0;
        button.tabIndex =
          slots.length &&
          (selectedDateIsAvailable ? key === this.schedulerSelectedDateKey : firstEnabled)
            ? 0
            : -1;
        if (slots.length) firstEnabled = false;
        if (key === todayKey) button.dataset.today = 'true';
        if (key === this.schedulerSelectedDateKey) {
          button.classList.add('is-selected');
        }
        button.addEventListener('click', () => this.chooseSchedulerDate(key), { signal: this.signal });
        button.addEventListener('keydown', (event) => this.handleSchedulerDateKeydown(event), {
          signal: this.signal,
        });
        fragment.append(button);
      }
      this.schedulerCalendar.replaceChildren(fragment);
    }

    handleSchedulerDateKeydown(event) {
      const moveBy = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[event.key];
      if (!moveBy && !['Home', 'End'].includes(event.key)) return;
      const dates = Array.from(this.schedulerCalendar.querySelectorAll('[data-scheduler-date]'));
      let index = dates.indexOf(event.currentTarget);
      if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = dates.length - 1;
      else index += moveBy;

      while (dates[index]?.disabled) index += Math.sign(moveBy || (event.key === 'Home' ? 1 : -1));
      if (!dates[index] || dates[index].disabled) return;
      event.preventDefault();
      dates.forEach((date) => (date.tabIndex = -1));
      dates[index].tabIndex = 0;
      dates[index].focus();
    }

    changeSchedulerMonth(direction) {
      const nextIndex = this.schedulerMonthIndex + direction;
      if (nextIndex < 0 || nextIndex >= this.schedulerMonths.length) return;
      this.measure('event', 'contact_booking_action', { action: direction < 0 ? 'month_previous' : 'month_next' });
      this.schedulerMonthIndex = nextIndex;
      this.schedulerDisplayedMonth = this.schedulerMonths[nextIndex];
      this.renderSchedulerCalendar();
      this.announceScheduler(`${this.schedulerMonthLabel?.textContent || ''} is now shown.`);
      this.schedulerCalendar?.querySelector('[data-scheduler-date]:not(:disabled)')?.focus({ preventScroll: true });
    }

    changeSchedulerTimezone() {
      const timezone = this.schedulerTimezoneSelect?.value;
      if (!timezone || timezone === this.schedulerTimezone) return;
      this.measure('event', 'contact_booking_action', { action: 'timezone_change' });
      this.schedulerTimezone = timezone;
      this.schedulerDisplayedMonth = '';
      this.schedulerSelectedDateKey = '';
      this.schedulerSelectedSlot = '';
      this.rebuildSchedulerMonths();
      this.renderSchedulerCalendar();
      this.showSchedulerPicker({ focus: false });
      this.announceScheduler(`Times are now shown in ${timezone.replaceAll('_', ' ')}.`);
    }

    chooseSchedulerDate(dateKey) {
      this.measure('event', 'contact_booking_action', { action: 'date_select' });
      this.schedulerSelectedDateKey = dateKey;
      this.schedulerSelectedSlot = '';
      this.renderSchedulerCalendar();
      this.renderSchedulerTimes();
      this.showSchedulerTimes();
    }

    renderSchedulerTimes() {
      if (!this.schedulerTimes || !this.schedulerSelectedDateKey) return;
      const slots = this.schedulerSlots
        .filter(
          (slot) => schedulerDateKey(new Date(slot.start_time), this.schedulerTimezone) === this.schedulerSelectedDateKey
        )
        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

      const sample = slots[0]?.start_time
        ? new Date(slots[0].start_time)
        : new Date(`${this.schedulerSelectedDateKey}T12:00:00Z`);
      if (this.schedulerSelectedDate) {
        this.schedulerSelectedDate.textContent = this.formatSchedulerDate(sample);
      }

      const fragment = document.createDocumentFragment();
      slots.forEach((slot) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'agency-contact-hero__scheduler-slot';
        button.textContent = this.formatSchedulerTime(new Date(slot.start_time));
        button.dataset.slotStart = slot.start_time;
        button.addEventListener('click', () => this.chooseSchedulerSlot(slot.start_time), { signal: this.signal });
        fragment.append(button);
      });
      this.schedulerTimes.replaceChildren(fragment);
    }

    chooseSchedulerSlot(startTime) {
      this.measure('event', 'contact_booking_action', { action: 'time_select' });
      this.schedulerSelectedSlot = startTime;
      if (this.schedulerSelectedTime) {
        this.schedulerSelectedTime.textContent = this.formatSchedulerDateTime(new Date(startTime));
      }

      const contactName = this.form?.querySelector('[name="contact[First name]"]')?.value?.trim();
      const contactEmail = this.form?.querySelector('[name="contact[email]"]')?.value?.trim();
      if (this.schedulerName && !this.schedulerName.value && contactName) this.schedulerName.value = contactName;
      if (this.schedulerEmail && !this.schedulerEmail.value && contactEmail) this.schedulerEmail.value = contactEmail;

      if (this.schedulerPicker) this.schedulerPicker.hidden = true;
      if (this.schedulerTimesPanel) this.schedulerTimesPanel.hidden = true;
      if (this.schedulerBookingForm) this.schedulerBookingForm.hidden = false;
      if (this.schedulerConfirmation) this.schedulerConfirmation.hidden = true;
      this.scheduler.dataset.schedulerState = 'details';
      this.measure('view', 'time_select');
      requestAnimationFrame(() => {
        const focusTarget = !this.schedulerName?.value
          ? this.schedulerName
          : !this.schedulerEmail?.value
            ? this.schedulerEmail
            : this.schedulerSubmit;
        focusTarget?.focus({ preventScroll: true });
      });
    }

    showSchedulerPicker({ focus = true } = {}) {
      if (this.schedulerPicker) this.schedulerPicker.hidden = false;
      if (this.schedulerTimesPanel) this.schedulerTimesPanel.hidden = true;
      if (this.schedulerBookingForm) this.schedulerBookingForm.hidden = true;
      if (this.schedulerConfirmation) this.schedulerConfirmation.hidden = true;
      if (this.scheduler) this.scheduler.dataset.schedulerState = 'picker';
      this.measure('view', 'change_date');
      if (focus) {
        requestAnimationFrame(() => {
          const selected = this.schedulerCalendar?.querySelector('.is-selected');
          const firstAvailable = this.schedulerCalendar?.querySelector('[data-scheduler-date]:not(:disabled)');
          (selected || firstAvailable || this.schedulerMonthLabel)?.focus?.({ preventScroll: true });
        });
      }
    }

    showSchedulerTimes() {
      if (!this.schedulerSelectedDateKey) {
        this.showSchedulerPicker();
        return;
      }
      if (this.schedulerPicker) this.schedulerPicker.hidden = true;
      if (this.schedulerTimesPanel) this.schedulerTimesPanel.hidden = false;
      if (this.schedulerBookingForm) this.schedulerBookingForm.hidden = true;
      if (this.schedulerConfirmation) this.schedulerConfirmation.hidden = true;
      if (this.scheduler) this.scheduler.dataset.schedulerState = 'times';
      this.measure('view', 'date_select');
      requestAnimationFrame(() => this.schedulerTimesHeading?.focus({ preventScroll: true }));
    }

    async submitSchedulerBooking(event) {
      event.preventDefault();
      if (!this.schedulerBookingForm || !this.schedulerSelectedSlot) return;
      if (!this.schedulerBookingForm.checkValidity()) {
        this.schedulerBookingForm.reportValidity();
        return;
      }

      const endpoint = this.schedulerApiUrl('/calendly/bookings');
      if (!endpoint) {
        this.measure('event', 'contact_booking_error', { error_code: 'unavailable' });
        this.showSchedulerFormError('Online booking is not configured. Please use the Calendly link below.');
        return;
      }

      this.showSchedulerFormError('');
      if (this.schedulerSubmit) this.schedulerSubmit.disabled = true;
      if (this.schedulerSubmitLabel) this.schedulerSubmitLabel.textContent = 'Confirming…';
      this.measure('event', 'contact_booking_submit', { action: 'submit' });

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          credentials: 'omit',
          cache: 'no-store',
          signal: this.signal,
          body: JSON.stringify({
            name: this.schedulerName.value,
            email: this.schedulerEmail.value,
            timezone: this.schedulerTimezone,
            start_time: this.schedulerSelectedSlot,
          }),
        });
        const payload = await response.json().catch(() => ({}));

        if (response.status === 409 || payload?.code === 'slot_unavailable') {
          this.measure('event', 'contact_booking_error', { error_code: 'slot_unavailable' });
          this.announceScheduler('That time was just taken. The available times have been refreshed.');
          await this.initializeScheduler(true);
          return;
        }
        if (!response.ok || !payload || typeof payload !== 'object' || Array.isArray(payload) || !Object.keys(payload).length) {
          const failure = new Error(payload?.error || 'We could not confirm the booking. Please try again.');
          failure.contactErrorCode = response.ok ? 'invalid_response' : 'http_error';
          throw failure;
        }

        this.measure('event', 'contact_booking_success', { action: 'success' });
        this.showSchedulerConfirmation(payload);
        this.measure('view', 'success');
        this.track('agency_scheduler_booking_complete', { scheduler_host: 'adam' });
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.measure('event', 'contact_booking_error', { error_code: error.contactErrorCode || (error instanceof TypeError ? 'network' : 'booking_failed') });
          this.showSchedulerFormError(error.message);
        }
      } finally {
        if (this.schedulerSubmit) this.schedulerSubmit.disabled = false;
        if (this.schedulerSubmitLabel) this.schedulerSubmitLabel.textContent = 'Confirm booking';
      }
    }

    showSchedulerConfirmation(payload) {
      if (this.schedulerPicker) this.schedulerPicker.hidden = true;
      if (this.schedulerTimesPanel) this.schedulerTimesPanel.hidden = true;
      if (this.schedulerBookingForm) this.schedulerBookingForm.hidden = true;
      if (this.schedulerConfirmation) this.schedulerConfirmation.hidden = false;
      if (this.scheduler) this.scheduler.dataset.schedulerState = 'confirmation';
      if (this.schedulerConfirmationTime) {
        this.schedulerConfirmationTime.textContent = this.formatSchedulerDateTime(
          new Date(payload.start_time || this.schedulerSelectedSlot)
        );
      }
      this.setSchedulerManagementLink(this.schedulerReschedule, payload.reschedule_url);
      this.setSchedulerManagementLink(this.schedulerCancel, payload.cancel_url);
      this.announceScheduler('Your CRO Mapping Call is booked.');
      requestAnimationFrame(() => this.schedulerConfirmation?.focus({ preventScroll: true }));
    }

    setSchedulerManagementLink(link, value) {
      if (!link) return;
      try {
        const url = new URL(value);
        const allowed =
          url.protocol === 'https:' && ['calendly.com', 'www.calendly.com'].includes(url.hostname);
        if (!allowed) throw new Error('Invalid management URL');
        link.href = url.toString();
        link.hidden = false;
      } catch (_error) {
        link.hidden = true;
        link.removeAttribute('href');
      }
    }

    showSchedulerLoading() {
      if (this.schedulerLoading) this.schedulerLoading.hidden = false;
      if (this.schedulerError) this.schedulerError.hidden = true;
      if (this.schedulerInterface) this.schedulerInterface.hidden = true;
      if (this.scheduler) this.scheduler.dataset.schedulerState = 'loading';
      this.measure('view', 'progress');
    }

    showSchedulerReady() {
      if (this.schedulerLoading) this.schedulerLoading.hidden = true;
      if (this.schedulerError) this.schedulerError.hidden = true;
      if (this.schedulerInterface) this.schedulerInterface.hidden = false;
      if (this.scheduler?.dataset.schedulerState === 'loading') this.scheduler.dataset.schedulerState = 'picker';
      this.measure('view', 'progress');
      if (!this.schedulerSlots?.length) this.measure('event', 'contact_booking_error', { error_code: 'no_slots' });
    }

    showSchedulerError(message) {
      if (this.schedulerLoading) this.schedulerLoading.hidden = true;
      if (this.schedulerInterface) this.schedulerInterface.hidden = true;
      if (this.schedulerError) this.schedulerError.hidden = false;
      if (this.schedulerErrorMessage) this.schedulerErrorMessage.textContent = message;
      if (this.scheduler) this.scheduler.dataset.schedulerState = 'error';
      this.measure('view', 'progress');
      this.measure('event', 'contact_booking_error', { error_code: 'unavailable' });
    }

    showSchedulerFormError(message) {
      if (!this.schedulerFormError) return;
      this.schedulerFormError.textContent = message;
      this.schedulerFormError.hidden = !message;
    }

    formatSchedulerDate(value) {
      return new Intl.DateTimeFormat('en-AU', {
        timeZone: this.schedulerTimezone,
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(value);
    }

    formatSchedulerTime(value) {
      return new Intl.DateTimeFormat('en-AU', {
        timeZone: this.schedulerTimezone,
        hour: 'numeric',
        minute: '2-digit',
      }).format(value);
    }

    formatSchedulerDateTime(value) {
      return `${this.formatSchedulerDate(value)} at ${this.formatSchedulerTime(value)} · ${this.schedulerTimezone.replaceAll('_', ' ')}`;
    }

    announceScheduler(message) {
      if (!this.schedulerStatus) return;
      this.schedulerStatus.textContent = '';
      requestAnimationFrame(() => {
        this.schedulerStatus.textContent = message;
      });
    }

    lockDrawerScroll() {
      const root = document.documentElement;
      if (this.drawerOwnsScrollLock) return;
      this.drawerOwnsScrollLock = !root.classList.contains('agency-contact-hero-drawer-open');
      if (!this.drawerOwnsScrollLock) return;
      this.drawerScrollPosition = window.scrollY;
      root.style.setProperty('--agency-drawer-page-top', `${-this.drawerScrollPosition}px`);
      root.classList.add('agency-contact-hero-drawer-open');

      // The keyboard changes the visual viewport independently of layout/dvh on mobile.
      const viewport = window.visualViewport;
      const syncViewport = () => {
        cancelAnimationFrame(this.drawerViewportFrame);
        this.drawerViewportFrame = requestAnimationFrame(() => {
          if (!this.drawerOwnsScrollLock) return;
          // Leave pinch zoom native; only fit the unzoomed keyboard viewport.
          const fitViewport = viewport && Math.abs(viewport.scale - 1) < 0.01;
          this.drawer.style.setProperty('--agency-drawer-visible-height', `${fitViewport ? viewport.height : window.innerHeight}px`);
          this.drawer.style.setProperty('--agency-drawer-visible-top', `${fitViewport ? viewport.offsetTop : 0}px`);
        });
      };
      viewport?.addEventListener('resize', syncViewport);
      viewport?.addEventListener('scroll', syncViewport);
      window.addEventListener('resize', syncViewport);
      this.drawerViewportCleanup = () => {
        viewport?.removeEventListener('resize', syncViewport);
        viewport?.removeEventListener('scroll', syncViewport);
        window.removeEventListener('resize', syncViewport);
        cancelAnimationFrame(this.drawerViewportFrame);
        this.drawer.style.removeProperty('--agency-drawer-visible-height');
        this.drawer.style.removeProperty('--agency-drawer-visible-top');
      };
      syncViewport();
    }

    unlockDrawerScroll(restorePosition = true) {
      if (!this.drawerOwnsScrollLock) return;
      this.drawerViewportCleanup?.();
      this.drawerViewportCleanup = null;
      const root = document.documentElement;
      root.classList.remove('agency-contact-hero-drawer-open');
      root.style.removeProperty('--agency-drawer-page-top');
      this.drawerOwnsScrollLock = false;
      if (restorePosition && Number.isFinite(this.drawerScrollPosition)) {
        window.scrollTo({ top: this.drawerScrollPosition, behavior: 'instant' });
      }
    }

    setupContactForm() {
      if (!this.form) return;

      this.steps = Array.from(this.form.querySelectorAll('[data-form-step]'));
      if (!this.steps.length) {
        if (this.form.querySelector('[data-shopify-contact-success]')) this.showConfirmation();
        return;
      }

      this.currentStep = 0;
      this.furthestStep = 0;
      this.stepSubmitButtons = Array.from(this.form.querySelectorAll('[data-form-step-submit]'));
      this.submitIdle = this.form.querySelector('[data-submit-idle]');
      this.submitLoading = this.form.querySelector('[data-submit-loading]');
      this.stepDots = Array.from(this.querySelectorAll('[data-form-step-dot]'));
      this.stepDots.forEach((dot, index) => {
        const number = document.createElement('span');
        number.className = 'agency-contact-hero__milestone-number';
        number.setAttribute('aria-hidden', 'true');
        number.textContent = String(index + 1);
        dot.replaceChildren(number);
      });
      this.steps.forEach((step) => {
        const helpText = step.dataset.stepHelp?.trim();
        if (!helpText || step.querySelector('[data-composer-help]')) return;
        const help = document.createElement('p');
        help.className = 'agency-contact-hero__composer-help';
        help.dataset.composerHelp = '';
        help.id = `${step.id}-help`;
        help.textContent = helpText;
        step.querySelector('legend')?.after(help);
        step.setAttribute('aria-describedby', help.id);
      });
      this.progressBar = this.querySelector('[data-progress-bar]');
      this.progressRegion = this.querySelector('[data-form-progress]');
      this.stepAnnouncer = this.form.querySelector('[data-step-announcer]');
      this.conversation = this.form.querySelector('[data-form-conversation]');
      this.conversationScroller = this.form.closest('[data-drawer-route-panel="form"]');
      this.transcript = this.form.querySelector('[data-form-transcript]');
      this.composer = this.form.querySelector('[data-form-composer]');
      this.composerSteps = this.form.querySelector('[data-form-steps]');
      this.otherOptions = Array.from(this.form.querySelectorAll('[data-other-option]'));
      this.otherInputs = Array.from(this.form.querySelectorAll('[data-other-input]'));
      this.multiChoiceSteps = Array.from(this.form.querySelectorAll('[data-multiple-choice]'));
      this.errorMessage = this.form.querySelector('[data-form-error]');
      this.fileInput = this.form.querySelector('[data-file-input]');
      if (this.fileInput && !this.fileInput.accept.includes('.txt')) this.fileInput.accept += ',.txt';
      this.fileName = this.form.querySelector('[data-file-name]');
      this.websiteInput = this.form.querySelector('[data-website-input]');
      this.messageInput = this.form.querySelector('textarea[name="contact[Message]"]');
      this.nameInput = this.form.querySelector('input[name="contact[First name]"]');
      this.emailInput = this.form.querySelector('input[name="contact[email]"]');
      this.sessionsNumeric = this.form.querySelector('[data-sessions-numeric]');
      this.recommendedService = this.form.querySelector('[data-recommended-service]');
      this.leadScore = this.form.querySelector('[data-lead-score]');
      this.leadTier = this.form.querySelector('[data-lead-tier]');
      this.fileNameDefault = this.fileName?.textContent || '';
      this.lastInteractionModality = 'pointer';
      this.isTransitioning = false;
      this.latestAnswerStep = null;
      this.presentedQuestionSteps = new Set();
      this.motionFinishers = new Set();

      this.captureFirstTouch();
      this.syncAllOtherChoices();
      this.syncAllMultiChoices();
      this.resizeMessageComposer();
      this.showStep(0, false);
      this.updateDerivedFields();

      this.form.addEventListener(
        'pointerdown',
        (event) => {
          this.lastInteractionModality = 'pointer';
          const choice = event.target.closest?.('.agency-contact-hero__choice');
          const radio = choice?.querySelector('input[type="radio"]');
          if (radio instanceof HTMLInputElement) {
            if (radio.checked) radio.dataset.confirmSelection = 'true';
            else delete radio.dataset.confirmSelection;
          }
        },
        { signal: this.signal }
      );
      this.form.addEventListener(
        'keydown',
        (event) => {
          this.lastInteractionModality = 'keyboard';
          if (
            !(event.target instanceof HTMLInputElement) ||
            event.target.type !== 'radio' ||
            !event.target.checked ||
            !['Enter', ' '].includes(event.key)
          ) {
            return;
          }

          const step = event.target.closest('[data-form-step]');
          if (step?.dataset.autoAdvance !== 'true' || this.steps[this.currentStep] !== step) return;
          event.preventDefault();
          window.clearTimeout(this.autoAdvanceTimer);
          this.autoAdvanceTimer = window.setTimeout(() => this.goForward(true), 120);
        },
        { signal: this.signal }
      );

      this.stepSubmitButtons.forEach((button) => {
        button.addEventListener(
          'click',
          (event) => {
            const step = button.closest('[data-form-step]');
            if (this.steps[this.currentStep] !== step) return;
            if (this.currentStep === this.steps.length - 1) this.submitFromCurrentStep(event.detail === 0);
            else this.goForward(true);
          },
          { signal: this.signal }
        );
      });
      this.stepDots.forEach((dot) => {
        dot.tabIndex = Number(dot.dataset.formStepDot) === 0 ? 0 : -1;
        dot.addEventListener(
          'click',
          (event) => this.goToStep(Number(dot.dataset.formStepDot), event.detail === 0),
          { signal: this.signal }
        );
      });
      this.querySelector('[data-form-step-dots]')?.addEventListener(
        'keydown',
        (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();

          const focusedIndex = Math.max(0, this.stepDots.indexOf(document.activeElement));
          let targetIndex = focusedIndex;
          if (event.key === 'Home') targetIndex = 0;
          else if (event.key === 'End') targetIndex = this.stepDots.length - 1;
          else if (event.key === 'ArrowLeft') targetIndex = (focusedIndex - 1 + this.stepDots.length) % this.stepDots.length;
          else targetIndex = (focusedIndex + 1) % this.stepDots.length;

          this.stepDots.forEach((stepDot, index) => {
            stepDot.tabIndex = index === targetIndex ? 0 : -1;
          });
          this.stepDots[targetIndex]?.focus({ preventScroll: true });
        },
        { signal: this.signal }
      );
      this.form.addEventListener('submit', (event) => this.handleSubmit(event), { signal: this.signal });
      this.fileInput?.addEventListener('change', () => this.updateFileName(), { signal: this.signal });
      this.form.querySelector('[data-files-remove]')?.addEventListener('click', () => {
        if (this.fileInput) this.fileInput.value = '';
        for (const document of this.webMcpDocuments?.values() || []) document.attached = false;
        this.renderWebMcpDocuments();
        this.updateFileName();
        this.fileInput?.focus({ preventScroll: true });
      }, { signal: this.signal });
      this.websiteInput?.addEventListener(
        'blur',
        () => {
          this.normalizeWebsiteInput();
          if (this.websiteInput.checkValidity()) this.clearClientInvalid(this.websiteInput);
        },
        { signal: this.signal }
      );
      this.websiteInput?.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'Enter' || event.isComposing) return;
          event.preventDefault();
          this.goForward(true);
        },
        { signal: this.signal }
      );
      this.messageInput?.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'Enter' || event.isComposing || (!event.metaKey && !event.ctrlKey)) return;
          event.preventDefault();
          if (this.currentStep === this.steps.length - 1) this.submitFromCurrentStep(true);
          else this.goForward(true);
        },
        { signal: this.signal }
      );
      this.messageInput?.addEventListener('input', () => this.resizeMessageComposer(), { signal: this.signal });
      this.nameInput?.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'Enter' || event.isComposing) return;
          event.preventDefault();
          this.goForward(true);
        },
        { signal: this.signal }
      );
      this.emailInput?.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'Enter' || event.isComposing) return;
          event.preventDefault();
          this.goForward(true);
        },
        { signal: this.signal }
      );
      this.transcript?.addEventListener(
        'click',
        (event) => {
          const editButton = event.target.closest('[data-conversation-edit]');
          if (!(editButton instanceof HTMLButtonElement)) return;
          this.editAnswer(Number(editButton.dataset.conversationEdit), event.detail === 0);
        },
        { signal: this.signal }
      );
      this.form.addEventListener(
        'click',
        (event) => {
          if (!(event.target instanceof HTMLInputElement) || event.target.type !== 'radio') return;
          if (event.target.dataset.confirmSelection !== 'true') return;
          delete event.target.dataset.confirmSelection;

          const step = event.target.closest('[data-form-step]');
          if (step?.dataset.autoAdvance !== 'true' || this.steps[this.currentStep] !== step) return;
          const moveFocus = this.lastInteractionModality === 'keyboard';

          window.clearTimeout(this.autoAdvanceTimer);
          this.autoAdvanceTimer = window.setTimeout(() => this.goForward(moveFocus), 120);
        },
        { signal: this.signal }
      );
      this.otherInputs.forEach((input) => {
        input.addEventListener(
          'input',
          () => {
            const step = input.closest('[data-form-step]');
            const otherOption = step?.querySelector('[data-other-option]');
            if (otherOption instanceof HTMLInputElement) this.syncOtherChoice(otherOption);
            if (step?.hasAttribute('data-multiple-choice')) this.syncMultiChoiceStep(step);
            this.updateDerivedFields();
          },
          { signal: this.signal }
        );
        input.addEventListener(
          'keydown',
          (event) => {
            if (event.key !== 'Enter' || event.isComposing) return;
            event.preventDefault();
            this.goForward(true);
          },
          { signal: this.signal }
        );
      });

      this.form.addEventListener(
        'change',
        (event) => {
          if (
            !(event.target instanceof HTMLInputElement) ||
            !['radio', 'checkbox'].includes(event.target.type)
          ) {
            return;
          }
          const activeStep = event.target.closest('[data-form-step]');
          const otherOption = activeStep?.querySelector('[data-other-option]');

          if (event.target.type === 'checkbox' && activeStep?.hasAttribute('data-multiple-choice')) {
            this.applyMultiChoiceExclusivity(activeStep, event.target);
            if (otherOption instanceof HTMLInputElement) {
              this.syncOtherChoice(
                otherOption,
                event.target === otherOption && otherOption.checked && this.lastInteractionModality === 'pointer'
              );
            }
            this.syncMultiChoiceStep(activeStep);
            this.updateDerivedFields();
            this.clearValidClientState(activeStep);
            this.refreshVisibleClientError(activeStep);
            return;
          }

          if (otherOption instanceof HTMLInputElement) {
            this.syncOtherChoice(
              otherOption,
              event.target === otherOption && this.lastInteractionModality === 'pointer'
            );
          }
          this.updateDerivedFields();
          this.clearValidClientState(activeStep);
          this.refreshVisibleClientError(activeStep);

          if (activeStep?.dataset.autoAdvance !== 'true') return;
          if (event.target === otherOption) return;
          if (this.lastInteractionModality !== 'pointer') return;

          window.clearTimeout(this.autoAdvanceTimer);
          this.autoAdvanceTimer = window.setTimeout(() => {
            if (this.steps[this.currentStep] === activeStep) this.goForward(false);
          }, 150);
        },
        { signal: this.signal }
      );

      this.form.addEventListener(
        'input',
        (event) => {
          if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
            this.syncTextFieldValidity(event.target);
            if (event.target.checkValidity()) this.clearClientInvalid(event.target);
            const activeStep = event.target.closest('[data-form-step]');
            this.refreshVisibleClientError(activeStep);
          }
        },
        { signal: this.signal }
      );
      this.reducedMotionQuery.addEventListener(
        'change',
        () => {
          if (this.reducedMotionQuery.matches && this.drawerRoute === 'form') this.startCurrentQuestionTyping();
        },
        { signal: this.signal }
      );
    }

    syncAllOtherChoices() {
      this.otherOptions?.forEach((option) => this.syncOtherChoice(option));
    }

    syncAllMultiChoices() {
      this.multiChoiceSteps?.forEach((step) => this.syncMultiChoiceStep(step));
    }

    applyMultiChoiceExclusivity(step, changedOption) {
      if (!(step instanceof HTMLElement) || !(changedOption instanceof HTMLInputElement) || !changedOption.checked) {
        return;
      }

      const options = Array.from(step.querySelectorAll('[data-multi-option]'));
      const exclusiveOption = options.find((option) => option.hasAttribute('data-exclusive-option'));

      if (changedOption === exclusiveOption) {
        options.forEach((option) => {
          if (option !== changedOption && !option.hasAttribute('data-other-option')) option.checked = false;
        });
      } else if (!changedOption.hasAttribute('data-other-option') && exclusiveOption instanceof HTMLInputElement) {
        exclusiveOption.checked = false;
      }
    }

    multiChoiceValues(step) {
      if (!(step instanceof HTMLElement)) return [];

      return Array.from(step.querySelectorAll('[data-multi-option]:checked'))
        .map((option) => {
          if (!(option instanceof HTMLInputElement)) return '';
          if (!option.hasAttribute('data-other-option')) return option.value.trim();

          const otherInput = step.querySelector('[data-other-input]');
          const otherValue = otherInput instanceof HTMLInputElement ? otherInput.value.trim() : '';
          return otherValue ? `Other: ${otherValue}` : '';
        })
        .filter(Boolean);
    }

    multiChoiceAnswer(step) {
      const options = Array.from(step.querySelectorAll('[data-multi-option]'));
      const otherValue = step.querySelector('[data-other-input]')?.value.trim() || '';
      const answers = options.flatMap((option, optionIndex) => {
        if (!option.checked) return [];
        if (option.hasAttribute('data-exclusive-option')) {
          return options.slice(0, optionIndex)
            .filter((item) => !item.hasAttribute('data-other-option') && !item.hasAttribute('data-exclusive-option'))
            .map((item) => item.value.trim());
        }
        if (option.hasAttribute('data-other-option')) return otherValue ? [`Other: ${otherValue}`] : [];
        return [option.value.trim()];
      }).filter(Boolean);
      return answers.map((answer, index) => `${index + 1}. ${answer}`).join('\n');
    }

    syncMultiChoiceStep(step) {
      if (!(step instanceof HTMLElement) || !step.hasAttribute('data-multiple-choice')) return;

      const options = Array.from(step.querySelectorAll('[data-multi-option]'));
      const exclusiveOption = options.find((option) => option.hasAttribute('data-exclusive-option'));
      if (exclusiveOption instanceof HTMLInputElement && exclusiveOption.checked) {
        options.forEach((option) => {
          if (option !== exclusiveOption && !option.hasAttribute('data-other-option')) option.checked = false;
        });
        const otherOption = step.querySelector('[data-other-option]');
        if (otherOption instanceof HTMLInputElement) this.syncOtherChoice(otherOption);
      }

      const firstOption = options[0];
      const values = this.multiChoiceValues(step);
      const hasSelection = options.some((option) => option instanceof HTMLInputElement && option.checked);
      const namedValue = step.querySelector('[data-multi-value]');
      const selectionStatus = step.querySelector('[data-multi-select-status]');

      if (namedValue instanceof HTMLInputElement) namedValue.value = values.join('; ');
      if (selectionStatus instanceof HTMLElement) {
        const selectedCount = options.filter((option) => option.checked).length;
        selectionStatus.textContent = selectedCount ? `${selectedCount} selected` : 'No options selected';
      }
      if (firstOption instanceof HTMLInputElement) {
        firstOption.setCustomValidity(hasSelection ? '' : 'Select at least one option.');
      }
    }

    syncOtherChoice(option, moveFocus = false) {
      if (!(option instanceof HTMLInputElement)) return;

      const step = option.closest('[data-form-step]');
      const composer = step?.querySelector('[data-other-composer]');
      const field = step?.querySelector('[data-other-field]');
      const input = step?.querySelector('[data-other-input]');
      if (!(composer instanceof HTMLElement) || !(field instanceof HTMLElement) || !(input instanceof HTMLInputElement)) return;

      const isSelected = option.checked;
      field.hidden = !isSelected;
      input.disabled = !isSelected;
      input.required = isSelected;
      input.setAttribute('aria-required', String(isSelected));
      option.setAttribute('aria-expanded', String(isSelected));

      const answer = input.value.trim();
      input.setCustomValidity(isSelected && !answer ? 'Please type your answer.' : '');
      option.value = isSelected && answer ? answer : option.dataset.otherDefaultValue || 'Other';

      if (isSelected && moveFocus) {
        requestAnimationFrame(() => {
          const bounds = field.getBoundingClientRect();
          const visibleBounds = this.conversationScroller.getBoundingClientRect();
          if (bounds.bottom > visibleBounds.bottom || bounds.top < visibleBounds.top) {
            field.scrollIntoView({ behavior: 'auto', block: 'nearest' });
          }
          input.focus({ preventScroll: true });
        });
      }
    }

    captureFirstTouch() {
      if (!this.form) return;
      Object.entries(VISIT_ATTRIBUTION.touch()).forEach(([key, value]) => {
        const input = this.form.querySelector(`[data-first-touch="${key}"]`);
        if (input) input.value = value || '';
      });
    }

    normalizeWebsiteInput() {
      if (!this.websiteInput) return;
      const value = this.websiteInput.value.trim();
      this.websiteInput.setCustomValidity('');
      if (!value) return;

      const normalizedValue = /^https?:\/\//i.test(value) ? value : `https://${value}`;
      this.websiteInput.value = normalizedValue;

      try {
        const parsedUrl = new URL(normalizedValue);
        const hasCompleteHostname = parsedUrl.hostname.includes('.') && !parsedUrl.hostname.startsWith('.') && !parsedUrl.hostname.endsWith('.');
        if (!['http:', 'https:'].includes(parsedUrl.protocol) || !hasCompleteHostname) {
          this.websiteInput.setCustomValidity('Enter a complete store URL, such as https://example.com.');
        }
      } catch (error) {
        this.websiteInput.setCustomValidity('Enter a complete store URL, such as https://example.com.');
      }
    }

    resizeMessageComposer() {
      if (!(this.messageInput instanceof HTMLTextAreaElement)) return;
      this.messageInput.style.height = 'auto';
      this.messageInput.style.height = `${Math.min(Math.max(this.messageInput.scrollHeight, 56), 176)}px`;
    }

    emailValidationMessage(value) {
      const domain = String(value || '').trim().split('@').pop()?.toLowerCase() || '';
      return /(^|\.)example\.(com|net|org)$|(^|\.)(example|invalid|test|localhost)$/.test(domain)
        ? 'Please use an email address that can receive replies. Example and placeholder email domains cannot receive your confirmation.'
        : '';
    }

    syncTextFieldValidity(field) {
      if (!(field instanceof HTMLInputElement) && !(field instanceof HTMLTextAreaElement)) return;
      if (!field.required || field.disabled) {
        if (field.dataset.whitespaceInvalid === 'true') {
          field.setCustomValidity('');
          delete field.dataset.whitespaceInvalid;
        }
        return;
      }

      if (field.hasAttribute('data-other-input')) {
        const step = field.closest('[data-form-step]');
        const otherOption = step?.querySelector('[data-other-option]');
        if (otherOption instanceof HTMLInputElement) this.syncOtherChoice(otherOption);
        return;
      }

      if (field.type === 'url') {
        field.setCustomValidity('');
        return;
      }

      if (field.type === 'email') {
        field.setCustomValidity(this.emailValidationMessage(field.value));
        return;
      }

      const isWhitespaceOnly = field.value.length > 0 && !field.value.trim();
      if (isWhitespaceOnly) {
        field.setCustomValidity('Please enter a response.');
        field.dataset.whitespaceInvalid = 'true';
      } else if (field.dataset.whitespaceInvalid === 'true') {
        field.setCustomValidity('');
        delete field.dataset.whitespaceInvalid;
      }
    }

    enquiryFiles() {
      return [...Array.from(this.fileInput?.files || []), ...Array.from(this.webMcpDocuments?.values() || []).filter((document) => document.attached).map((document) => document.file)];
    }

    webMcpDocumentState() {
      return Array.from(this.webMcpDocuments?.values() || []).map(({ id, title, file, text, revision, attached, missingQuestions, sourceBriefRevision }) => ({
        id, title, filename: file.name, bytes: file.size, mime_type: file.type, revision, attached, text,
        missing_questions: missingQuestions,
        ...(sourceBriefRevision ? { based_on_project_brief_revision: sourceBriefRevision } : {}),
      }));
    }

    clearWebMcpDocuments() {
      this.webMcpDocuments?.forEach((document) => { if (document.url) URL.revokeObjectURL(document.url); });
      this.webMcpDocuments = new Map();
      this.webMcpReview = null;
      this.querySelector('[data-webmcp-documents]')?.remove();
    }

    assertWebMcpDocumentEditable() {
      if (!this.form?.isConnected || this.webMcpSubmission) throw new TypeError('This enquiry has already been sent or attempted. Check its status before changing documents.');
      if (this.drawerNavigationBusy() || this.inlineEdit || this.drawerState === 'closing') throw new TypeError('The form is busy. Finish the current interaction and retry.');
    }

    async prepareWebMcpDocument({ id, title, text, data, attached, missingQuestions = [], sourceBriefRevision = null }) {
      this.assertWebMcpDocumentEditable();
      if (!['project_brief', 'internal_decision_pack'].includes(id) || typeof text !== 'string' || !text.trim() || text.length > 32_000) throw new TypeError('The draft document is invalid or too long.');
      const previous = this.webMcpDocuments?.get(id);
      const changed = previous?.text !== text || JSON.stringify(previous?.data) !== JSON.stringify(data);
      const file = changed ? new File([text], `blend-${id.replaceAll('_', '-')}.md`, { type: 'text/markdown;charset=utf-8' }) : previous.file;
      const document = { id, title, text, data, attached, file, missingQuestions, sourceBriefRevision, revision: (previous?.revision || 0) + (changed ? 1 : 0), url: changed ? null : previous.url };
      const files = [...Array.from(this.fileInput?.files || []), ...Array.from(this.webMcpDocuments?.values() || []).filter((item) => item.id !== id && item.attached).map((item) => item.file), ...(attached ? [file] : [])];
      const error = this.fileValidationMessage(files);
      if (error) throw new TypeError(error);
      await this.openWebMcpRoute('form');
      this.assertWebMcpDocumentEditable();
      if (changed && previous?.url) URL.revokeObjectURL(previous.url);
      this.webMcpDocuments ||= new Map();
      this.webMcpDocuments.set(id, document);
      this.renderWebMcpDocuments();
      if ((changed && (attached || previous?.attached)) || Boolean(previous?.attached) !== attached) this.updateFileName();
      const step = this.steps.length - 1;
      this.presentedQuestionSteps.add(step);
      this.showStep(step, false);
      this.finishCurrentQuestionTyping(false);
      return this.getWebMcpState();
    }

    renderWebMcpDocuments() {
      this.querySelector('[data-webmcp-documents]')?.remove();
      const step = this.fileInput?.closest('[data-form-step]');
      if (!step || !this.webMcpDocuments?.size) return;
      const container = document.createElement('div');
      container.dataset.webmcpDocuments = '';
      container.style.cssText = 'width:100%;min-width:0;margin:16px 0;display:grid;gap:12px;text-align:left;';
      for (const draft of this.webMcpDocuments.values()) {
        const card = document.createElement('div');
        card.dataset.webmcpDocument = draft.id;
        card.style.cssText = 'min-width:0;padding:14px;border:1px solid currentColor;border-radius:12px;font-size:14px;line-height:1.5;';
        const details = document.createElement('details');
        const summary = document.createElement('summary');
        summary.textContent = `${draft.title} · draft ${draft.revision}`;
        summary.style.cssText = 'cursor:pointer;font-weight:600;';
        const preview = document.createElement('pre');
        preview.textContent = draft.text;
        preview.style.cssText = 'font:inherit;white-space:pre-wrap;overflow-wrap:anywhere;max-height:22rem;overflow:auto;padding:12px 0;margin:0;';
        preview.tabIndex = 0;
        details.append(summary, preview);
        const controls = document.createElement('div');
        controls.style.cssText = 'display:flex;gap:12px;align-items:center;flex-wrap:wrap;margin-top:10px;';
        if (!draft.url) draft.url = URL.createObjectURL(draft.file);
        const download = document.createElement('a');
        download.href = draft.url;
        download.download = draft.file.name;
        download.textContent = `Download ${draft.title.toLowerCase()}`;
        download.style.cssText = 'color:inherit;text-decoration:underline;';
        const label = document.createElement('label');
        label.style.cssText = 'display:flex;align-items:center;gap:6px;cursor:pointer;';
        const include = document.createElement('input');
        include.type = 'checkbox';
        include.dataset.webmcpDocumentControl = '';
        include.checked = draft.attached;
        include.style.cssText = 'appearance:auto;width:16px;height:16px;margin:0;';
        include.addEventListener('change', () => {
          try {
            this.assertWebMcpDocumentEditable();
            const files = [...Array.from(this.fileInput?.files || []), ...Array.from(this.webMcpDocuments.values()).filter((item) => item.id !== draft.id && item.attached).map((item) => item.file), ...(include.checked ? [draft.file] : [])];
            const error = this.fileValidationMessage(files);
            if (error) throw new TypeError(error);
            draft.attached = include.checked;
            this.updateFileName();
          } catch (error) {
            include.checked = draft.attached;
            this.showError(error.message);
          }
        });
        label.append(include, document.createTextNode('Include with enquiry'));
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.textContent = 'Remove draft';
        remove.setAttribute('aria-label', `Remove ${draft.title.toLowerCase()}`);
        remove.style.cssText = 'font:inherit;color:inherit;background:transparent;border:0;padding:0;text-decoration:underline;cursor:pointer;';
        remove.addEventListener('click', () => {
          try {
            this.assertWebMcpDocumentEditable();
            URL.revokeObjectURL(draft.url);
            this.webMcpDocuments.delete(draft.id);
            this.renderWebMcpDocuments();
            this.updateFileName();
          } catch (error) { this.showError(error.message); }
        });
        controls.append(download, label, remove);
        const note = document.createElement('p');
        note.textContent = 'Saved in this page only. Review before sending; download a copy to keep it.';
        note.style.cssText = 'margin:8px 0 0;font-size:12px;';
        card.append(details, controls, note);
        container.append(card);
      }
      step.append(container);
    }

    fileValidationMessage(files = this.enquiryFiles()) {
      if (!files.length) return '';
      if (files.length > 10) return 'Please choose up to 10 files.';
      if (files.reduce((total, file) => total + file.size, 0) > 10_000_000) {
        return 'Please choose files totalling no more than 10 MB.';
      }
      const allowedExtensions = new Set(['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'png', 'jpg', 'jpeg', 'webp', 'txt', 'md']);
      if (files.some((file) => !allowedExtensions.has(file.name.split('.').pop()?.toLowerCase() || ''))) {
        return 'Choose PDF, Word, Excel, CSV, PNG, JPG, WebP, text, or Markdown files.';
      }
      if (!this.form.dataset.formEndpoint?.trim()) {
        return 'File attachments are temporarily unavailable. Remove the files to send your enquiry.';
      }
      return '';
    }

    syncStepCustomValidity(step) {
      if (!(step instanceof HTMLElement)) return;
      step.querySelectorAll('input, textarea').forEach((field) => this.syncTextFieldValidity(field));
      if (step.hasAttribute('data-multiple-choice')) this.syncMultiChoiceStep(step);
      if (this.websiteInput && step.contains(this.websiteInput)) this.normalizeWebsiteInput();

      if (this.fileInput && step.contains(this.fileInput)) {
        this.fileInput.setCustomValidity(this.fileValidationMessage());
      }
    }

    setClientInvalid(field) {
      if (!(field instanceof HTMLElement) || !this.errorMessage?.id) return;

      const describedBy = new Set((field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
      describedBy.add(this.errorMessage.id);
      field.setAttribute('aria-describedby', Array.from(describedBy).join(' '));
      field.setAttribute('aria-invalid', 'true');
      field.dataset.clientInvalid = 'true';
      field
        .closest('.agency-contact-hero__choice, .agency-contact-hero__input-composer, .agency-contact-hero__other-composer, .agency-contact-hero__other-field')
        ?.classList.add('has-error');
    }

    clearClientInvalid(field) {
      if (!(field instanceof HTMLElement) || field.dataset.clientInvalid !== 'true') return;

      const describedBy = (field.getAttribute('aria-describedby') || '')
        .split(/\s+/)
        .filter((id) => id && id !== this.errorMessage?.id);
      if (describedBy.length) field.setAttribute('aria-describedby', describedBy.join(' '));
      else field.removeAttribute('aria-describedby');

      field.removeAttribute('aria-invalid');
      delete field.dataset.clientInvalid;
      field
        .closest('.agency-contact-hero__choice, .agency-contact-hero__input-composer, .agency-contact-hero__other-composer, .agency-contact-hero__other-field')
        ?.classList.remove('has-error');
    }

    clearValidClientState(step) {
      if (!(step instanceof HTMLElement)) return;
      step.querySelectorAll('[data-client-invalid="true"]').forEach((field) => {
        if (field.checkValidity()) this.clearClientInvalid(field);
      });
    }

    refreshVisibleClientError(step) {
      if (!(step instanceof HTMLElement)) return;

      const invalidField = step.querySelector('[data-client-invalid="true"]');
      if (invalidField && !invalidField.checkValidity()) {
        this.showError(invalidField.validationMessage || 'Complete this answer before continuing.');
      } else {
        this.clearError();
      }
    }

    validateStep(stepIndex) {
      const step = this.steps[stepIndex];
      if (!step) return false;

      if (stepIndex === this.currentStep) this.finishCurrentQuestionTyping(false);
      this.syncStepCustomValidity(step);

      const fields = Array.from(step.querySelectorAll('input:not([disabled]), textarea:not([disabled]), select:not([disabled])'));
      const invalidField = fields.find((field) => !field.checkValidity());

      if (invalidField) {
        this.measure('validation', invalidField, stepIndex);
        this.showError(invalidField.validationMessage || 'Complete this answer before continuing.');
        this.setClientInvalid(invalidField);
        invalidField.focus({ preventScroll: true });
        invalidField.closest('.agency-contact-hero__choice, .agency-contact-hero__input-composer, .agency-contact-hero__other-composer, .agency-contact-hero__other-field')
          ?.scrollIntoView({ behavior: this.reducedMotionQuery?.matches ? 'auto' : 'smooth', block: 'nearest' });
        return false;
      }

      step.querySelectorAll('[data-client-invalid="true"]').forEach((field) => this.clearClientInvalid(field));
      this.clearError();
      return true;
    }

    firstInvalidStepIndex(endExclusive = this.steps.length) {
      this.normalizeWebsiteInput();
      const limit = Math.max(0, Math.min(endExclusive, this.steps.length));

      for (let index = 0; index < limit; index += 1) {
        const step = this.steps[index];
        this.syncStepCustomValidity(step);
        const fields = Array.from(step.querySelectorAll('input:not([disabled]), textarea:not([disabled]), select:not([disabled])'));
        if (fields.some((field) => !field.checkValidity())) return index;
      }

      return -1;
    }

    async revealInvalidStep(stepIndex, moveFocus = true) {
      if (stepIndex !== this.currentStep) {
        const direction = stepIndex > this.currentStep ? 'forward' : 'back';
        await this.transitionToStep(stepIndex, moveFocus, direction);
      }

      return this.validateStep(stepIndex);
    }

    questionTitle(step) {
      const firstName = this.nameInput?.value.trim().split(/\s+/)[0] || '';
      return (step?.dataset.stepTitle || '').replace(/\{\{?\s*first[_ ]name\s*\}?\}/gi, () => firstName);
    }

    editAnswer(stepIndex) {
      if (!Number.isInteger(stepIndex) || this.isTransitioning || this.form.dataset.finalizing === 'true') return;
      if (stepIndex < 0 || stepIndex >= this.currentStep) return;
      if (this.inlineEdit) this.finishInlineEdit(false, true);
      this.finishCurrentQuestionTyping(false);
      window.clearTimeout(this.autoAdvanceTimer);
      const step = this.steps[stepIndex];
      const trigger = this.transcript.querySelector(`[data-conversation-edit="${stepIndex}"]`);
      const response = trigger?.closest('.agency-contact-hero__transcript-response');
      if (!response) return;
      const placeholder = document.createComment('answer-editor');
      step.before(placeholder);
      const fields = Array.from(step.querySelectorAll('input, textarea, select'));
      const snapshot = fields.map((field) => ({ field, value: field.value, checked: field.checked }));
      const editor = document.createElement('div');
      editor.className = 'agency-contact-hero__inline-editor';
      const error = document.createElement('p');
      error.className = 'agency-contact-hero__inline-error';
      error.setAttribute('role', 'alert');
      error.hidden = true;
      const actions = document.createElement('div');
      actions.className = 'agency-contact-hero__inline-actions';
      ['Cancel', 'Save'].forEach((label) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = label;
        button.addEventListener('click', () => this.finishInlineEdit(label === 'Save', true));
        actions.append(button);
      });
      editor.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' || (event.key === 'Enter' && !event.isComposing && !(event.target instanceof HTMLTextAreaElement))) {
          event.preventDefault();
          event.stopPropagation();
          this.finishInlineEdit(event.key === 'Enter', true);
        }
      }, true);
      step.hidden = false;
      step.inert = false;
      editor.append(step, error, actions);
      response.replaceChildren(editor);
      this.inlineEdit = { stepIndex, step, placeholder, snapshot, error };
      this.measure('beginEdit', stepIndex);
      this.composer.inert = true;
      fields.find((field) => !field.disabled && !field.closest('[hidden]') && field.type !== 'hidden' && field.type !== 'file')?.focus({ preventScroll: true });
      editor.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }

    finishInlineEdit(save, render = false) {
      if (!this.inlineEdit) return true;
      const { stepIndex, step, placeholder, snapshot, error } = this.inlineEdit;
      if (save) {
        this.normalizeWebsiteInput();
        this.syncStepCustomValidity(step);
        const invalid = Array.from(step.querySelectorAll('input, textarea, select')).find((field) => !field.disabled && !field.checkValidity());
        if (invalid) {
          this.measure('validation', invalid, stepIndex);
          error.textContent = invalid.validationMessage;
          error.hidden = false;
          invalid.focus({ preventScroll: true });
          return false;
        }
      } else {
        snapshot.forEach(({ field, value, checked }) => {
          if (field.type !== 'file') field.value = value;
          if (typeof checked === 'boolean') field.checked = checked;
          field.setCustomValidity('');
          this.clearClientInvalid(field);
        });
      }
      step.hidden = true;
      placeholder.replaceWith(step);
      this.inlineEdit = null;
      this.measure('finishEdit', stepIndex, save);
      this.composer.inert = false;
      this.syncAllOtherChoices();
      this.syncAllMultiChoices();
      this.updateDerivedFields();
      if (render) {
        const scrollTop = this.conversationScroller.scrollTop;
        this.renderConversationTranscript();
        this.conversationScroller.scrollTop = scrollTop;
        this.transcript.querySelector(`[data-conversation-edit="${stepIndex}"]`)?.focus({ preventScroll: true });
        this.stepAnnouncer.textContent = save ? 'Answer updated. Your other answers are unchanged.' : 'Edit cancelled.';
      }
      return true;
    }

    goForward(moveFocus = true) {
      window.clearTimeout(this.autoAdvanceTimer);
      if (this.isTransitioning || this.inlineEdit) return;
      if (!this.validateStep(this.currentStep)) return;
      if (this.currentStep >= this.steps.length - 1) return;

      this.latestAnswerStep = this.currentStep;
      this.measure('completeStep', this.currentStep);
      this.track('agency_contact_step_completed', {
        step: this.currentStep + 1,
        question: this.steps[this.currentStep]?.dataset.questionKey || 'details',
      });
      this.transitionToStep(this.currentStep + 1, moveFocus, 'forward');
    }

    async goToStep(stepIndex, moveFocus = true) {
      window.clearTimeout(this.autoAdvanceTimer);
      if (!Number.isInteger(stepIndex) || this.isTransitioning) return;

      const targetStep = Math.max(0, Math.min(stepIndex, this.steps.length - 1));
      if (targetStep === this.currentStep) return;

      if (targetStep > this.currentStep) {
        const invalidStep = this.firstInvalidStepIndex(targetStep);
        if (invalidStep !== -1) {
          await this.revealInvalidStep(invalidStep, moveFocus);
          return;
        }
        this.latestAnswerStep = this.currentStep;
      } else {
        this.editAnswer(targetStep);
        return;
      }

      const direction = targetStep > this.currentStep ? 'forward' : 'back';
      await this.transitionToStep(targetStep, moveFocus, direction);
    }

    async submitFromCurrentStep(moveFocus = true) {
      window.clearTimeout(this.autoAdvanceTimer);
      if (this.isTransitioning || this.form.dataset.finalizing === 'true') return;

      const invalidStep = this.firstInvalidStepIndex();
      if (invalidStep !== -1) {
        await this.revealInvalidStep(invalidStep, moveFocus);
        return;
      }

      this.finishCurrentQuestionTyping(false);
      this.form.dataset.finalizing = 'true';
      this.syncDrawerNavigation();
      this.latestAnswerStep = this.currentStep;
      this.renderConversationTranscript(true);
      this.stepSubmitButtons.forEach((button) => {
        button.disabled = true;
      });
      if (this.conversation) {
        requestAnimationFrame(() => {
          if (this.inlineEdit) return;
          this.conversationScroller.scrollTo({
            top: this.conversationScroller.scrollHeight,
            behavior: this.reducedMotionQuery?.matches ? 'auto' : 'smooth',
          });
        });
      }
      if (!this.reducedMotionQuery?.matches) {
        await new Promise((resolve) => window.setTimeout(resolve, 220));
      }
      this.form.requestSubmit();
    }

    async transitionToStep(stepIndex, moveFocus = true, direction = 'forward') {
      const targetStep = Math.max(0, Math.min(stepIndex, this.steps.length - 1));
      if (targetStep === this.currentStep || this.isTransitioning) return;

      if (this.reducedMotionQuery?.matches) {
        this.showStep(targetStep, moveFocus);
        if (this.drawer?.open && this.drawerState !== 'closing' && this.drawerRoute === 'form') {
          this.startCurrentQuestionTyping(moveFocus);
        }
        return;
      }

      const currentStep = this.steps[this.currentStep];
      const leavingClass = `is-leaving-${direction}`;
      this.isTransitioning = true;
      this.form.dataset.transitioning = 'true';
      this.syncDrawerNavigation();

      try {
        currentStep?.classList.add(leavingClass);
        await this.waitForStepMotion(currentStep, 140);
        currentStep?.classList.remove(leavingClass);

        if (!this.isConnected) return;

        const previousHeight = this.composerSteps?.getBoundingClientRect().height;
        this.showStep(targetStep, moveFocus);
        this.animateComposerHeight(previousHeight);
        if (this.drawer?.open && this.drawerState !== 'closing' && this.drawerRoute === 'form') {
          this.startCurrentQuestionTyping(moveFocus);
        }
      } finally {
        currentStep?.classList.remove(leavingClass);
        this.isTransitioning = false;
        delete this.form.dataset.transitioning;
        this.syncDrawerNavigation();
      }
    }

    animateComposerHeight(previousHeight, reveal = false) {
      const steps = this.composerSteps;
      this.composerMotion?.cancel();
      if (!steps || this.reducedMotionQuery?.matches || !previousHeight) return;
      const nextHeight = steps.getBoundingClientRect().height;
      const frames = [
        { height: `${previousHeight}px`, ...(reveal ? { opacity: 0, transform: 'translateY(4px)' } : {}) },
        { height: `${nextHeight}px`, ...(reveal ? { opacity: 1, transform: 'translateY(0)' } : {}) },
      ];
      const motion = steps.animate(frames, { duration: 280, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' });
      this.composerMotion = motion;
      motion.finished.then(() => {
        if (this.composerMotion !== motion) return;
        this.composerMotion = null;
        if (!this.inlineEdit && this.drawerRoute === 'form') {
          this.conversationScroller?.scrollTo({ top: this.conversationScroller.scrollHeight, behavior: 'smooth' });
        }
      }).catch(() => {});
    }

    waitForStepMotion(element, duration) {
      if (!element) return Promise.resolve();

      return new Promise((resolve) => {
        let settled = false;
        let fallbackTimer;

        const finish = () => {
          if (settled) return;
          settled = true;
          element.removeEventListener('animationend', handleAnimationEnd);
          window.clearTimeout(fallbackTimer);
          this.motionFinishers.delete(finish);
          resolve();
        };
        const handleAnimationEnd = (event) => {
          if (event.target === element) finish();
        };

        element.addEventListener('animationend', handleAnimationEnd);
        fallbackTimer = window.setTimeout(finish, duration + 120);
        this.motionFinishers.add(finish);
      });
    }

    showStep(stepIndex, moveFocus = true) {
      window.clearTimeout(this.questionTypingTimer);
      this.composerMotion?.cancel();
      this.currentStep = Math.max(0, Math.min(stepIndex, this.steps.length - 1));
      this.furthestStep = Math.max(this.furthestStep || 0, this.currentStep);
      this.syncDrawerNavigation();
      const shouldTypeQuestion = !this.reducedMotionQuery?.matches && !this.presentedQuestionSteps.has(this.currentStep);

      this.steps.forEach((step, index) => {
        const isActive = index === this.currentStep;
        step.hidden = !isActive;
        step.inert = isActive && shouldTypeQuestion;
        step.classList.toggle('is-active', isActive);
      });

      const activeStep = this.steps[this.currentStep];
      const stepTitle = this.questionTitle(activeStep);
      const stepHelp = activeStep?.dataset.stepHelp?.trim() || '';
      const progress = this.steps.length > 1 ? (this.currentStep / (this.steps.length - 1)) * 100 : 100;

      const progressAnnouncement = `Question ${this.currentStep + 1} of ${this.steps.length}`;
      if (this.progressBar) this.progressBar.style.width = `${progress}%`;
      if (this.progressRegion) {
        this.progressRegion.setAttribute('aria-valuenow', String(this.currentStep + 1));
        this.progressRegion.setAttribute('aria-valuemax', String(this.steps.length));
        this.progressRegion.setAttribute('aria-valuetext', `${progressAnnouncement}: ${stepTitle}`);
      }
      if (this.stepAnnouncer) {
        this.stepAnnouncer.textContent = [this.pendingResetAnnouncement, progressAnnouncement, stepTitle, stepHelp]
          .filter(Boolean)
          .join('. ');
      }
      this.pendingResetAnnouncement = '';
      this.stepDots.forEach((dot, index) => {
        const isCurrent = index === this.currentStep;
        dot.classList.toggle('is-complete', index < this.currentStep);
        dot.classList.toggle('is-current', isCurrent);
        dot.tabIndex = isCurrent ? 0 : -1;
        if (isCurrent) dot.setAttribute('aria-current', 'step');
        else dot.removeAttribute('aria-current');
      });

      if (shouldTypeQuestion) this.form.dataset.questionTyping = 'true';
      else delete this.form.dataset.questionTyping;
      this.renderConversationTranscript();
      this.clearError();

      this.measure('view', 'progress');

      if (this.conversation) {
        requestAnimationFrame(() => {
          if (this.inlineEdit) return;
          this.conversationScroller.scrollTo({
            top: this.conversationScroller.scrollHeight,
            behavior: this.reducedMotionQuery?.matches ? 'auto' : 'smooth',
          });
        });
      }
    }

    answerForStep(step) {
      if (!(step instanceof HTMLElement)) return '';

      const questionKey = step.dataset.questionKey;
      if (questionKey) {
        if (step.hasAttribute('data-multiple-choice')) {
          return this.multiChoiceAnswer(step);
        }

        const selected = step.querySelector('input[type="radio"]:checked');
        if (!(selected instanceof HTMLInputElement)) return '';

        const otherInput = step.querySelector('[data-other-input]');
        const answer =
          selected.hasAttribute('data-other-option') && otherInput instanceof HTMLInputElement
            ? otherInput.value.trim()
            : selected.value.trim();
        if (!answer) return '';

        return answer;
      }

      const website = step.querySelector('input[name="contact[Company website URL]"]');
      if (website instanceof HTMLInputElement && website.value.trim()) return website.value.trim();

      const message = step.querySelector('textarea[name="contact[Message]"]');
      if (message instanceof HTMLTextAreaElement) {
        const files = this.enquiryFiles();
        const answerParts = [];
        if (message.value.trim()) answerParts.push(message.value.trim());
        files.forEach((file) => answerParts.push(`Attached: ${file.name}`));
        return answerParts.join('\n');
      }

      const name = step.querySelector('input[name="contact[First name]"]');
      if (name instanceof HTMLInputElement && name.value.trim()) return name.value.trim();

      const email = step.querySelector('input[name="contact[email]"]');
      if (email instanceof HTMLInputElement && email.value.trim()) return email.value.trim();

      return '';
    }

    renderConversationTranscript(includeCurrentAnswer = false) {
      this.finishInlineEdit(false);
      this.steps.forEach((step, index) => {
        const title = this.questionTitle(step);
        const legend = step.querySelector('legend');
        if (legend) legend.textContent = title;
        this.stepDots[index]?.setAttribute('aria-label', `Go to step ${index + 1} of ${this.steps.length}: ${title}`);
      });
      if (!this.transcript) return;

      this.transcript.replaceChildren();

      const completedStepCount = Math.min(
        this.steps.length,
        this.currentStep + (includeCurrentAnswer ? 1 : 0)
      );

      for (let index = 0; index < completedStepCount; index += 1) {
        const step = this.steps[index];
        const answer = this.answerForStep(step);
        if (!answer) continue;

        const turn = document.createElement('li');
        turn.className = 'agency-contact-hero__transcript-turn';

        const prompt = document.createElement('div');
        prompt.className = 'agency-contact-hero__transcript-prompt';

        const question = document.createElement('p');
        question.className = 'agency-contact-hero__transcript-question';
        question.textContent = this.questionTitle(step) || `Question ${index + 1}`;

        prompt.append(question);

        const responseWrap = document.createElement('div');
        responseWrap.className = 'agency-contact-hero__transcript-response';
        if (index === this.latestAnswerStep) responseWrap.classList.add('is-latest');

        const answerBubble = document.createElement('div');
        answerBubble.className = 'agency-contact-hero__transcript-answer';
        answerBubble.tabIndex = 0;

        const responseText = document.createElement('p');
        responseText.className = 'agency-contact-hero__transcript-answer-copy';
        responseText.textContent = answer;

        const editButton = document.createElement('button');
        editButton.className = 'agency-contact-hero__transcript-edit';
        editButton.type = 'button';
        editButton.dataset.conversationEdit = String(index);
        editButton.innerHTML =
          '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4.75 19.25h3.5L18.5 9l-3.5-3.5L4.75 15.75v3.5Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="m13.75 6.75 3.5 3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
        editButton.setAttribute('aria-label', `Edit answer for ${this.questionTitle(step) || `question ${index + 1}`}`);

        answerBubble.append(responseText);
        responseWrap.append(answerBubble, editButton);
        turn.append(prompt, responseWrap);
        this.transcript.append(turn);
      }

      const activeStep = this.steps[this.currentStep];
      if (activeStep && !includeCurrentAnswer) {
        const currentTurn = document.createElement('li');
        currentTurn.className = 'agency-contact-hero__transcript-turn is-current';
        currentTurn.dataset.currentQuestion = '';

        const currentPrompt = document.createElement('div');
        currentPrompt.className = 'agency-contact-hero__transcript-prompt';

        const currentQuestion = document.createElement('p');
        currentQuestion.className = 'agency-contact-hero__transcript-question';
        const currentQuestionText = this.questionTitle(activeStep) || `Question ${this.currentStep + 1}`;
        currentQuestion.dataset.currentTypewriter = '';
        currentQuestion.dataset.typewriterText = currentQuestionText;
        currentQuestion.setAttribute('aria-label', currentQuestionText);
        currentQuestion.textContent =
          this.reducedMotionQuery?.matches || this.presentedQuestionSteps.has(this.currentStep) ? currentQuestionText : '';
        currentPrompt.append(currentQuestion);


        currentTurn.append(currentPrompt);
        this.transcript.append(currentTurn);
      }

      this.transcript.hidden = false;
      this.latestAnswerStep = null;
    }

    focusActiveComposer() {
      const activeStep = this.steps?.[this.currentStep];
      if (activeStep?.querySelector('[data-form-choices]')) {
        const question = this.transcript?.querySelector('[data-current-typewriter]');
        question?.setAttribute('tabindex', '-1');
        requestAnimationFrame(() => question?.focus({ preventScroll: true }));
        return;
      }
      const focusTarget = activeStep?.querySelector(
        'input:not([type="hidden"]):not([type="file"]):not([disabled]), textarea:not([disabled]), select:not([disabled]), button[data-form-step-submit]:not([disabled])'
      );
      requestAnimationFrame(() => focusTarget?.focus({ preventScroll: true }));
    }

    finishCurrentQuestionTyping(focusComposer = false) {
      window.clearTimeout(this.questionTypingTimer);

      const question = this.transcript?.querySelector('[data-current-typewriter]');
      if (question instanceof HTMLElement) {
        question.textContent = question.dataset.typewriterText || '';
        question.classList.remove('is-typing');
      }

      const wasTyping = this.form.dataset.questionTyping === 'true';
      const previousHeight = this.composerSteps?.getBoundingClientRect().height;
      if (this.steps?.[this.currentStep]) this.steps[this.currentStep].inert = false;
      this.presentedQuestionSteps?.add(this.currentStep);
      delete this.form.dataset.questionTyping;
      if (wasTyping) this.animateComposerHeight(previousHeight, true);
      if (this.conversation) {
        requestAnimationFrame(() => {
          if (this.inlineEdit) return;
          this.conversationScroller.scrollTo({
            top: this.conversationScroller.scrollHeight,
            behavior: this.reducedMotionQuery?.matches ? 'auto' : 'smooth',
          });
        });
      }
      if (focusComposer) this.focusActiveComposer();
    }

    startCurrentQuestionTyping(focusComposer = false) {
      window.clearTimeout(this.questionTypingTimer);
      if (this.drawerRoute !== 'form') return;

      const question = this.transcript?.querySelector('[data-current-typewriter]');
      if (!(question instanceof HTMLElement)) {
        this.finishCurrentQuestionTyping(focusComposer);
        return;
      }

      const fullText = question.dataset.typewriterText || '';
      if (this.reducedMotionQuery?.matches || this.presentedQuestionSteps.has(this.currentStep) || !fullText) {
        this.finishCurrentQuestionTyping(focusComposer);
        return;
      }

      const typingStep = this.currentStep;
      const characters = Array.from(fullText);
      let characterIndex = 0;
      question.textContent = '';
      question.classList.add('is-typing');
      this.form.dataset.questionTyping = 'true';

      const typeNextCharacter = () => {
        if (
          !this.isConnected ||
          this.currentStep !== typingStep ||
          this.transcript?.querySelector('[data-current-typewriter]') !== question
        ) {
          question.classList.remove('is-typing');
          return;
        }

        characterIndex += 1;
        question.textContent = characters.slice(0, characterIndex).join('');
        if (characterIndex >= characters.length) {
          this.finishCurrentQuestionTyping(focusComposer);
          return;
        }

        const typedCharacter = characters[characterIndex - 1];
        const delay = /[.!?]/.test(typedCharacter) ? 90 : /[,;:]/.test(typedCharacter) ? 55 : 28;
        this.questionTypingTimer = window.setTimeout(typeNextCharacter, delay);
      };

      const openingDelay = typingStep === 0 ? 220 : 120;
      this.questionTypingTimer = window.setTimeout(typeNextCharacter, openingDelay);
    }

    selectedQuestion(questionKey) {
      return this.form.querySelector(`[data-question-key="${questionKey}"] input[type="radio"]:checked`);
    }

    updateDerivedFields() {
      const growthStep = this.form.querySelector('[data-question-key="growth_objective"]');
      const growth = growthStep?.hasAttribute('data-multiple-choice')
        ? growthStep.querySelector('[data-multi-option]:checked')
        : this.selectedQuestion('growth_objective');
      const position = this.selectedQuestion('cro_position');
      const sessions = this.selectedQuestion('sessions');
      const timeline = this.selectedQuestion('timeline');
      const sessionIndex = Number(sessions?.dataset.optionIndex || 0);
      const positionIndex = Number(position?.dataset.optionIndex || 0);
      const timelineIndex = Number(timeline?.dataset.optionIndex || 0);
      const growthIndex = growth ? 1 : 0;

      const score = this.calculateLeadScore(growthIndex, positionIndex, sessionIndex, timelineIndex);
      const tier = score >= 7 ? 'High fit' : score >= 4 ? 'Qualified' : 'Nurture';

      if (this.sessionsNumeric) this.sessionsNumeric.value = sessions?.dataset.sessionsValue || '';
      if (this.recommendedService) this.recommendedService.value = '';
      if (this.leadScore) this.leadScore.value = String(score);
      if (this.leadTier) this.leadTier.value = tier;

      return { score, tier };
    }

    calculateLeadScore(growthIndex, positionIndex, sessionIndex, timelineIndex) {
      const trafficScore = { 1: 0, 2: 1, 3: 2, 4: 4, 5: 0 }[sessionIndex] || 0;
      const urgencyScore = { 1: 3, 2: 2, 3: 1, 4: 0 }[timelineIndex] || 0;
      return trafficScore + urgencyScore + (growthIndex ? 1 : 0) + (positionIndex ? 1 : 0);
    }

    updateFileName() {
      this.webMcpReview = null;
      if (!this.fileName) return;
      const files = this.enquiryFiles();
      this.fileName.textContent = files.length ? files.map((file) => file.name).join(', ') : this.fileNameDefault;
      this.fileName.classList.toggle('has-file', files.length > 0);
      const removeButton = this.form.querySelector('[data-files-remove]');
      if (removeButton) removeButton.hidden = !files.length;
      const validationMessage = this.fileValidationMessage();
      this.fileInput?.setCustomValidity(validationMessage);
      if (validationMessage) {
        this.showError(validationMessage);
        this.setClientInvalid(this.fileInput);
      } else {
        this.clearClientInvalid(this.fileInput);
        this.clearError();
      }
    }

    webMcpFields() {
      const textFields = new Map([
        ['contact[First name]', 'first_name'], ['contact[email]', 'email'],
        ['contact[Company website URL]', 'website_url'], ['contact[Message]', 'message'],
      ]);
      return (this.steps || []).map((step) => {
        const input = Array.from(step.querySelectorAll('input, textarea')).find((field) => textFields.has(field.name));
        const key = step.dataset.questionKey || textFields.get(input?.name);
        const options = Array.from(step.querySelectorAll('input[type="radio"], input[type="checkbox"]:not([data-webmcp-document-control])'));
        const multiple = step.hasAttribute('data-multiple-choice');
        const other = step.querySelector('[data-other-input]');
        const choiceValue = (option) => option.hasAttribute('data-other-option') ? (other?.value.trim() || '') : option.value;
        const selected = options.filter((option) => option.checked).map(choiceValue);
        const value = input ? input.value : multiple ? selected : selected[0] || '';
        const invalid = Array.from(step.querySelectorAll('input,textarea,select')).find((field) => !field.disabled && field.willValidate && !field.validity.valid);
        const required = input ? input.required : true;
        const empty = Array.isArray(value) ? !value.length || value.some((item) => !item.trim()) : !String(value || '').trim();
        return { key, step, input, options, other, multiple, value, required, invalid, complete: !invalid && (!required || !empty) };
      }).filter((field) => field.key);
    }

    webMcpSnapshot() {
      return JSON.stringify({
        fields: this.webMcpFields().map(({ key, value }) => [key, value]),
        files: this.enquiryFiles().map((file) => [file.name, file.size, file.lastModified]),
        documents: this.webMcpDocumentState().filter((document) => document.attached).map(({ id, revision, text }) => ({ id, revision, text })),
      });
    }

    getWebMcpState() {
      this.syncTextFieldValidity(this.emailInput);
      const fields = this.webMcpFields();
      const accepted = this.webMcpSubmission?.status === 'accepted';
      const available = Boolean(this.form?.isConnected && fields.length);
      const busy = Boolean(this.drawerNavigationBusy() || this.inlineEdit || this.drawerOpenRequest || ['opening', 'closing'].includes(this.drawerState));
      const missing = fields.filter((field) => !field.complete).map((field) => field.key);
      const attachmentError = this.fileValidationMessage();
      if (attachmentError) missing.push('attachments');
      const ready = available && !busy && !missing.length && Boolean(this.form.dataset.formEndpoint) && !this.webMcpSubmission;
      if (ready) {
        const snapshot = this.webMcpSnapshot();
        if (snapshot !== this.webMcpReview?.snapshot) this.webMcpReview = { snapshot, token: crypto.randomUUID() };
      }
      return {
        available, open: Boolean(this.drawer?.open), route: this.drawerRoute || 'start', busy,
        fields: fields.map(({ key, step, input, options, multiple, value, required, complete }) => ({
          field: key, question: this.questionTitle(step), type: options.length ? (multiple ? 'multiple_choice' : 'choice') : input?.type || 'text',
          required, value, complete,
          ...(options.length ? { choices: options.filter((option) => !option.hasAttribute('data-other-option')).map((option) => option.value), allows_other: true } : {}),
        })),
        missing_or_invalid_fields: missing,
        attachments: this.enquiryFiles().map((file) => ({ name: file.name, bytes: file.size })),
        generated_documents: this.webMcpDocumentState(),
        attachment_error: attachmentError || null,
        email_error: this.emailValidationMessage(this.emailInput?.value) || null,
        ready_to_submit: ready, review_token: ready ? this.webMcpReview.token : null,
        submitted: accepted, submission_status: this.webMcpSubmission?.status || 'not_submitted',
        receipt_id: accepted ? this.webMcpSubmission.id : null,
        delivery_status: accepted ? 'accepted_by_enquiry_endpoint' : 'not_confirmed',
        next_step: accepted ? 'The enquiry endpoint accepted this enquiry. Downstream CRM and email delivery are not independently confirmed by this page.'
          : this.webMcpSubmission?.status === 'unknown' ? 'Delivery is uncertain. Do not automatically resubmit; check with Blend first.'
          : missing.length ? 'Ask the visitor for missing answers. Existing answers will be preserved.'
          : 'Review the enquiry with the visitor. Submit only after their explicit agreement to send it to Blend Commerce.',
      };
    }

    async openWebMcpRoute(route) {
      if (this.drawerNavigationBusy() || this.inlineEdit || this.drawerState === 'closing') throw new TypeError('The drawer is busy. Finish the current interaction and retry.');
      if (!this.drawerRoutePanels?.has(route)) throw new TypeError('This drawer route is unavailable.');
      // Mark the source without leaving an agent marker on a later human opening.
      if (this.drawer?.open) this.measure('agentEntry');
      else this.pendingMeasurementEntry = 'agent';
      this.setDrawerRoute(route, { animate: false, focus: false });
      try { await this.openContactDrawer(null, false, true, document.activeElement); }
      finally { this.pendingMeasurementEntry = null; }
      if (this.drawerState === 'opening') {
        await new Promise((resolve) => {
          const started = Date.now();
          const check = () => {
            if (this.drawerState !== 'opening' || Date.now() - started > 1500) resolve();
            else window.setTimeout(check, 30);
          };
          check();
        });
      }
      if (!this.drawer?.open) throw new Error('The contact drawer did not open.');
      if (route === 'form') this.finishCurrentQuestionTyping(false);
    }

    async prepareWebMcpEnquiry(values) {
      if (!this.form?.isConnected || this.webMcpSubmission) throw new TypeError('This enquiry has already been sent or attempted. Check its status before starting another.');
      if (this.drawerNavigationBusy() || this.inlineEdit) throw new TypeError('The form is busy. Finish the current interaction and retry.');
      const fields = this.webMcpFields();
      const changes = Object.entries(values).map(([key, supplied]) => {
        const field = fields.find((item) => item.key === key);
        if (!field) throw new TypeError(`The ${key} field is unavailable in this form.`);
        if (field.input) {
          let value = supplied.trim();
          if (key === 'website_url' && value && !/^https?:\/\//i.test(value)) value = `https://${value}`;
          if (key === 'website_url' && value) {
            let url;
            try { url = new URL(value); } catch { throw new TypeError('Enter a complete website URL.'); }
            if (!['https:', 'http:'].includes(url.protocol) || !url.hostname.includes('.') || url.username || url.password) throw new TypeError('Enter a complete public website URL.');
          }
          if (key === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) throw new TypeError('Enter a valid email address.');
          if (key === 'email' && this.emailValidationMessage(value)) throw new TypeError(this.emailValidationMessage(value));
          const probe = field.input.cloneNode();
          probe.value = value;
          if ((field.required && !value) || !probe.validity.valid) throw new TypeError(`Invalid ${key}.`);
          return { field, value };
        }
        const answers = (Array.isArray(supplied) ? supplied : [supplied]).map((value) => value.trim());
        if (!answers.length || answers.some((answer) => !answer || answer.length > 180)) throw new TypeError(`Provide a valid ${key} answer.`);
        const selected = [], otherAnswers = [];
        for (const answer of answers) {
          const option = field.options.find((item) => !item.hasAttribute('data-other-option') && item.value.toLowerCase() === answer.toLowerCase());
          if (option) selected.push(option); else otherAnswers.push(answer);
        }
        if (otherAnswers.length > 1 || (otherAnswers.length && !field.other)) throw new TypeError(`Use one Other answer for ${key}.`);
        if (!field.multiple && answers.length > 1) throw new TypeError(`Choose one ${key} answer.`);
        if (selected.some((option) => option.hasAttribute('data-exclusive-option')) && answers.length > 1) throw new TypeError('Choose All of the above by itself, or select individual challenges.');
        return { field, selected, otherAnswer: otherAnswers[0] || '' };
      });
      await this.openWebMcpRoute('form');
      window.clearTimeout(this.autoAdvanceTimer);
      for (const change of changes) {
        const { field } = change;
        if (field.input) field.input.value = change.value;
        else {
          field.options.forEach((option) => { option.checked = change.selected.includes(option) || (option.hasAttribute('data-other-option') && Boolean(change.otherAnswer)); });
          if (field.other) field.other.value = change.otherAnswer;
        }
      }
      this.webMcpReview = null;
      this.syncAllOtherChoices();
      this.syncAllMultiChoices();
      this.normalizeWebsiteInput();
      this.updateDerivedFields();
      this.resizeMessageComposer();
      const invalid = this.firstInvalidStepIndex();
      const next = invalid === -1 ? this.steps.length - 1 : invalid;
      this.presentedQuestionSteps.add(next);
      this.showStep(next, false);
      this.finishCurrentQuestionTyping(false);
      return { ...this.getWebMcpState(), updated_fields: changes.map(({ field }) => field.key) };
    }

    async submitWebMcpEnquiry({ confirmed, review_token }, options = {}) {
      if (this.webMcpSubmission) return this.getWebMcpState();
      const state = this.getWebMcpState();
      if (!state.ready_to_submit) throw new TypeError('Complete all required enquiry fields before submitting.');
      if (confirmed !== true) throw new TypeError('The visitor must explicitly agree to send this enquiry to Blend Commerce.');
      if (review_token !== this.webMcpReview?.token || this.webMcpSnapshot() !== this.webMcpReview?.snapshot) throw new TypeError('The enquiry changed. Review the latest fields and use the current review token.');
      if (options.signal?.aborted) throw new DOMException('Submission cancelled before sending.', 'AbortError');
      await this.handleSubmit({ preventDefault() {} });
      return this.getWebMcpState();
    }

    buildEnquiryPayload() {
      this.captureFirstTouch();
      const fields = new FormData(this.form);
      const files = this.enquiryFiles();
      this.submissionId ||= window.crypto?.randomUUID?.() || `blend-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const payload = new FormData();
      const text = (key) => String(fields.get(key) || '').trim();
      const values = {
        submission_id: this.submissionId,
        is_test: 'false',
        first_name: text('contact[First name]'),
        email: text('contact[email]'),
        website: text('contact[Company website URL]'),
        message: text('contact[Message]'),
        sessions_numeric: text('contact[Average monthly sessions]'),
        recommended_service: text('contact[Recommended service]'),
        lead_score: text('contact[Lead score]'),
        lead_tier: text('contact[Lead tier]'),
        company_fax: text('company_fax'),
        source_url: window.location.href,
        first_touch_url: text('first_touch_url'),
        utm_source: text('utm_source'),
        utm_medium: text('utm_medium'),
        utm_campaign: text('utm_campaign'),
        attachment_count: String(files.length),
        attachment_names: files.map((file) => file.name).join('\n'),
      };
      this.steps.forEach((step) => {
        if (step.dataset.questionKey) values[step.dataset.questionKey] = this.answerForStep(step);
      });
      Object.entries(values).forEach(([key, value]) => payload.append(key, value));
      // Separate field names prevent multipart parsers from retaining only the last file.
      for (let index = 0; index < 10; index += 1) {
        const file = files[index];
        if (file) payload.append(`attachment_${index + 1}`, file, file.name);
        else payload.append(`attachment_${index + 1}`, '');
      }
      return payload;
    }

    async handleSubmit(event) {
      event.preventDefault();
      const invalidStep = this.firstInvalidStepIndex();
      if (invalidStep !== -1) {
        event.preventDefault();
        await this.revealInvalidStep(invalidStep, true);
        return;
      }

      const outcome = this.updateDerivedFields();
      const endpoint = this.form.dataset.formEndpoint?.trim();
      if (!endpoint) {
        this.measure('event', 'contact_submit_error', { error_code: 'unavailable' });
        this.setSubmitting(false);
        delete this.form.dataset.finalizing;
        this.renderConversationTranscript();
        this.showError('Our enquiry service is not connected yet. Please try again shortly.');
        return;
      }

      if (this.form.dataset.submitting === 'true') return;

      this.measure('completeStep', this.currentStep);
      this.measure('event', 'contact_submit_attempt', { action: 'submit' });
      this.setSubmitting(true);
      this.webMcpSubmission = { status: 'submitting' };
      this.clearError();
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 90_000);

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          body: this.buildEnquiryPayload(),
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });

        let result = {};
        try {
          result = await response.json();
        } catch (error) {
          result = {};
        }

        if (!response.ok || result.status !== 'success' || !result.id) {
          const failure = new Error('We could not send your enquiry. Please try again.');
          failure.contactErrorCode = response.ok ? 'invalid_response' : 'http_error';
          throw failure;
        }

        this.webMcpSubmission = { status: 'accepted', id: String(result.id) };
        this.measure('event', 'contact_submit_success', { action: 'success' });
        this.track('agency_contact_submit_success', { transport: 'zapier', ...outcome });

        this.showConfirmation();
        this.trackConfirmedEnquiry(result.id);
      } catch (error) {
        this.webMcpSubmission = { status: 'unknown' };
        this.showError(error.name === 'AbortError' ? 'Sending is taking longer than expected. Your answers and files are still here; please try again.' : 'We could not send your enquiry. Your answers and files are still here; please try again.');
        this.track('agency_contact_submit_error', { transport: 'zapier' });
        this.measure('event', 'contact_submit_error', { error_code: error.name === 'AbortError' ? 'timeout' : error.contactErrorCode || (error instanceof TypeError ? 'network' : 'submission_failed') });
        this.setSubmitting(false);
        delete this.form.dataset.finalizing;
        this.syncDrawerNavigation();
        this.renderConversationTranscript();
      } finally {
        window.clearTimeout(timeout);
      }
    }

    setSubmitting(isSubmitting) {
      this.form.dataset.submitting = String(isSubmitting);
      this.syncDrawerNavigation();
      this.form.setAttribute('aria-busy', String(isSubmitting));
      this.stepSubmitButtons.forEach((button) => {
        button.disabled = isSubmitting;
      });
      this.stepDots.forEach((dot) => {
        dot.disabled = isSubmitting;
      });

      if (this.submitIdle) this.submitIdle.hidden = isSubmitting;
      if (this.submitLoading) this.submitLoading.hidden = !isSubmitting;
      const finalSubmitButton = this.submitIdle?.closest('[data-form-step-submit]');
      if (finalSubmitButton) finalSubmitButton.setAttribute('aria-label', isSubmitting ? 'Sending enquiry' : 'Send enquiry');
    }

    showConfirmation() {
      if (!this.confirmation || !this.form?.isConnected) return;
      if (this.drawerProgress) this.drawerProgress.hidden = true;
      this.form.dataset.submitting = 'false';
      delete this.form.dataset.finalizing;
      this.confirmation.hidden = false;
      this.form.replaceWith(this.confirmation);
      this.syncDrawerNavigation();
      this.confirmation.focus({ preventScroll: true });
      this.measure('view', 'success');
    }

    trackConfirmedEnquiry(submissionId) {
      if (!submissionId || window.Shopify?.designMode) return;
      if (!this.measurement?.canTrackEnquirySuccess()) return;
      this.trackedEnquiryIds ||= new Set();
      if (this.trackedEnquiryIds.has(submissionId)) return;

      // Reuse GA4's existing key event without a redirect or synthetic page view.
      // Use the existing Google tag/consent setup; never send enquiry field values.
      try {
        window.dataLayer = window.dataLayer || [];
        const send = typeof window.gtag === 'function'
          ? window.gtag
          : function () { window.dataLayer.push(arguments); };
        send('event', 'form_submitted', {
          send_to: 'G-C71KQBEKW5',
          form_name: 'contact_enquiry',
          form_location: 'drawer',
        });
        this.trackedEnquiryIds.add(submissionId);
      } catch (_error) {
        // A blocked analytics tag must never turn an accepted enquiry into an error.
      }
    }

    showError(message) {
      if (!this.errorMessage) return;
      this.errorMessage.textContent = message;
      this.errorMessage.hidden = false;
    }

    clearError() {
      if (!this.errorMessage) return;
      this.errorMessage.textContent = '';
      this.errorMessage.hidden = true;
    }

    track(eventName, detail = {}) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, ...detail });
    }

    measure(method, ...args) {
      try { return this.measurement?.[method]?.(...args); }
      catch (_error) { /* A measurement failure must never affect enquiries or bookings. */ }
    }
  }

  customElements.define('agency-contact-drawer', AgencyContactDrawer);
}
